import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useApi } from "../../hooks/useApi";
import { useCurrency } from "../../hooks/useCurrency";
import {
  Menu,
  X,
  Zap,
  BarChart3,
  Check,
  Star,
  ArrowUp,
  Brain,
  TrendingUp,
  Shield,
  Clock,
  Users,
  Info,
  RotateCcw,
  Download,
  Play,
  AlertCircle,
  Leaf,
  AlertTriangle,
  Activity,
  Code,
  LineChart,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  FileText,
  RotateCw,
  Network,
  Building2,
  ChevronDown,
  Plus,
  Minus,
  PlusCircle,
  XCircle,
  HelpCircle,
  Send,
  Settings,
  Rocket,
  Target,
  Search,
  Globe,
  Key,
  ExternalLink,
  Package,
  User,
  UserCog,
  Cog,
  MessageSquare,
  Ban,
  KeyRound,
} from "lucide-react";
import AlternateLanding from "./AlternateLanding";
import {
  cardEntranceVariants,
  floatingCardVariants,
  decorativeFloatVariants,
  heroVariants,
  statsVariants,
  buttonHoverVariants,
  containerVariants,
  useCountUp,
} from "../../utils/landingAnimations";
import { useTheme } from "../../contexts/ThemeContext";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import Footer from "../../components/Footer/Footer";
import Testimonials from "../../components/Testimonials/Testimonials";
import { getFreshS3Url } from "../../utils/s3Image";
import AskTeamGridAI from "../../components/AskTeamGridAI/AskTeamGridAI";
import EngagementPopup from "../../components/EngagementPopup/EngagementPopup";

const Landing = () => {
  const [selectedFeature, setSelectedFeature] = useState(0);
  const [activeValuePanel, setActiveValuePanel] = useState("intelligence");
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [hoveredFaqIndex, setHoveredFaqIndex] = useState(-1);
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();
  const [blogs, setBlogs] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(true);
  const [showAlternateLanding, setShowAlternateLanding] = useState(false);
  const [currentPricingSlide, setCurrentPricingSlide] = useState(0);
  const { formatPrice, symbol, getPrice } = useCurrency();

  // When logo is clicked (state from PublicHeader), show main landing and scroll hero into view
  useEffect(() => {
    if (location.state?.showMainLanding) {
      setShowAlternateLanding(false);
      navigate("/", { replace: true, state: {} });
      // Scroll hero section to top after paint so main landing opens with hero visible
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const hero = document.getElementById("landing-hero");
          if (hero) hero.scrollIntoView({ block: "start", behavior: "instant" });
          else window.scrollTo(0, 0);
        });
      });
    }
  }, [location.state?.showMainLanding, navigate]);

  const { mode, setMode } = useTheme();

  // Force light theme on landing page and set landing palette
  useEffect(() => {
    setMode("light");
    localStorage.setItem("teamgrid-theme", "light");
    document.documentElement.classList.remove("dark");
    document.documentElement.setAttribute("data-theme", "light");
    document.documentElement.setAttribute("data-page", "landing");

    // Always start from top on load
    window.scrollTo(0, 0);
    if (window.history.scrollRestoration) {
      window.history.scrollRestoration = "manual";
    }

    return () => {
      document.documentElement.removeAttribute("data-page");
    };
  }, [setMode]);

  // Scroll to pricing section when URL has #pricing (after starting from top)
  useEffect(() => {
    if (window.location.hash === "#pricing") {
      const el = document.getElementById("pricing");
      if (el) {
        const t = setTimeout(
          () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
          100,
        );
        return () => clearTimeout(t);
      }
    }
  }, []);

  // Fetch blogs
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setBlogsLoading(true);
        const response = await api.get("/api/v1/blogs", {
          params: { limit: 3 },
        });
        const fetchedBlogs = response.data.data.blogs;

        // Generate fresh presigned URLs for all featured images
        const blogsWithFreshUrls = await Promise.all(
          fetchedBlogs.map(async (blog) => {
            if (blog.featuredImage) {
              const freshUrl = await getFreshS3Url(blog.featuredImage);
              return { ...blog, featuredImage: freshUrl || blog.featuredImage };
            }
            return blog;
          }),
        );

        setBlogs(blogsWithFreshUrls);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setBlogsLoading(false);
      }
    };
    fetchBlogs();
  }, [api]);

  // Features data
  const features = [
    {
      id: 0,
      title: "AI Readiness Gaps",
      icon: Brain,
      iconColor: "text-blue-400",
      description:
        "See where your team still relies on manual work — even after being trained or told to automate.",
      details:
        "TeamGrid identifies patterns where team members bypass available automation tools. We analyse repeated manual tasks, compare them against existing workflows and highlight training gaps or usability problems preventing AI adoption.",
      example:
        "Multiple team members still create reports manually despite existing Zapier workflows.",
    },
    {
      id: 1,
      title: "Adoption v/s Outcome Patterns",
      icon: TrendingUp,
      iconColor: "text-green-400",
      description:
        "Understand the gap between tool adoption and actual productivity outcomes.",
      details:
        "Track which tools your team uses versus which ones actually drive results. Identify where adoption doesn't translate to better outcomes.",
      example:
        "Team uses 5 project management tools but only 2 show measurable productivity gains.",
    },
    {
      id: 2,
      title: "Privacy Protection",
      icon: Shield,
      iconColor: "text-purple-400",
      description:
        "Ensure your team's privacy while gaining actionable insights.",
      details:
        "TeamGrid respects privacy boundaries while providing meaningful analytics. We show patterns without exposing sensitive individual data.",
      example:
        "Aggregate productivity trends visible without revealing personal work habits.",
    },
    {
      id: 3,
      title: "Daily Work Summary & Real Signals",
      icon: Clock,
      iconColor: "text-yellow-400",
      description: "Get daily insights that matter, not just activity logs.",
      details:
        "Receive meaningful summaries of work patterns, not just time tracking. Identify real productivity signals versus noise.",
      example:
        "Daily summary highlights focus time blocks, not just hours logged.",
    },
    {
      id: 4,
      title: "Growth vs. Operations Balance",
      icon: Users,
      iconColor: "text-pink-400",
      description: "Balance strategic growth work with operational efficiency.",
      details:
        "Identify when teams are too focused on operations versus growth initiatives. Help rebalance efforts for sustainable scaling.",
      example:
        "Team spends 80% on maintenance, only 20% on new feature development.",
    },
  ];

  // Mock data for team members
  const teamMembers = [
    {
      name: "Rinkal V.",
      role: "Sr. PHP Developer",
      productivity: 85,
      avatar: "RV",
      color: "bg-green-primary",
    },
    {
      name: "Darshan R.",
      role: "React Developer",
      productivity: 85,
      avatar: "DR",
      color: "bg-teal-primary",
    },
    {
      name: "Ayush P.",
      role: "Python Developer",
      productivity: 51,
      avatar: "AP",
      color: "bg-orange-warning",
    },
  ];

  // Number counter animations
  const trackedHours = useCountUp(4000, 1.5);
  const productivityBoost = useCountUp(40, 1.5);
  const userRating = 4.8; // Static for now

  const baseUrl = "https://teamgrid.ai";
  const ogImage = `${baseUrl}/og/og-home.webp`;
  const isProduction = process.env.NODE_ENV === "production";

  const structuredData = useMemo(() => [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": "https://teamgrid.ai/#organization",
      name: "TeamGrid",
      url: "https://teamgrid.ai/",
      logo: "https://teamgrid.ai/logo-teamgrid.svg",
      sameAs: ["https://www.linkedin.com/company/teamgrid-ai"],
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": "https://teamgrid.ai/#website",
      url: "https://teamgrid.ai/",
      name: "TeamGrid",
      publisher: { "@id": "https://teamgrid.ai/#organization" },
    },
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": "https://teamgrid.ai/#webpage",
      url: "https://teamgrid.ai/",
      name: "TeamGrid - AI Workforce Intelligence Platform",
      description:
        "AI workforce intelligence platform that detects operational issues early, compares employee performance with context, and delivers actionable insights in minutes.",
      isPartOf: { "@id": "https://teamgrid.ai/#website" },
      about: { "@id": "https://teamgrid.ai/#software" },
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "@id": "https://teamgrid.ai/#software",
      name: "TeamGrid",
      operatingSystem: "Web",
      applicationCategory: "BusinessApplication",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is TeamGrid?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "TeamGrid is an AI-powered team intelligence platform that analyses real work patterns and productivity signals to generate summaries, insights, and automation recommendations for teams.",
          },
        },
        {
          "@type": "Question",
          name: "Does TeamGrid invade privacy?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "No, TeamGrid is built on a privacy-first foundation. It does not record screenshots, keystrokes, webcam feeds, content, or personal data. It tracks only work signals such as app usage, focus time, and task engagement to generate insights without spying.",
          },
        },
        {
          "@type": "Question",
          name: "How does the tracking work?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "TeamGrid installs a lightweight desktop agent for Windows or macOS. Admins can choose user-controlled start/stop tracking or an always-on mode. The agent runs efficiently with minimal system impact and sends only productivity signals to the dashboard.",
          },
        },
        {
          "@type": "Question",
          name: "Can I export reports?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes, productivity summaries, dashboards, and analytics can be exported in PDF or CSV formats for offline review or sharing.",
          },
        },
        {
          "@type": "Question",
          name: "Can I cancel or change my subscription anytime?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Absolutely. You can upgrade, downgrade, or cancel your subscription at any time through your dashboard without penalties.",
          },
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        // {
        //   "@type": "Question",
        //   name: "Is there a free trial available?",
        //   acceptedAnswer: {
        //     "@type": "Answer",
        //     text: "Yes, TeamGrid offers a 5-day free trial with access to core analytics and productivity insights so teams can explore features without risk.",
        //   },
        // },
        {
          "@type": "Question",
          name: "What pricing plans does TeamGrid offer?",
          acceptedAnswer: {
            "@type": "Answer",
            // FREE_PLAN_HIDDEN: free-plan wording removed from FAQ schema. Original text kept below to restore. See FREE_PLAN_TOGGLE.md
            // text: "TeamGrid offers a free plan for up to five users with basic tracking. Paid plans start at three dollars per user and include more advanced insights with no hidden fees.",
            text: "TeamGrid plans start at three dollars per user and include advanced insights with no hidden fees. Start with a free trial.",
          },
        },
      ],
    },
  ], []);

  // SEO: set document head (no external head library required)
  useEffect(() => {
    const title = "TeamGrid - AI Workforce Intelligence Platform for Employee Productivity & Team Analytics";
    document.title = title;

    const setMeta = (attr, key, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    const setLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"][href]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    setMeta("name", "description", "TeamGrid is a privacy-first workforce intelligence platform that turns real work data into clear team productivity analytics and AI recommendations, helping leaders fix issues without surveillance.");
    setLink("canonical", "https://teamgrid.ai/");
    if (isProduction) {
      setMeta("name", "robots", "index, follow");
      setMeta("name", "googlebot", "max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    } else {
      setMeta("name", "robots", "noindex, nofollow");
    }
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", "https://teamgrid.ai/");
    setMeta("property", "og:title", "TeamGrid - AI Workforce Intelligence Platform");
    setMeta("property", "og:description", "Detect issues early. Compare performance with AI context. Get actionable workforce insights in minutes.");
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("property", "og:image:alt", "TeamGrid - AI Workforce Intelligence Platform");
    setMeta("property", "og:site_name", "TeamGrid");
    setMeta("property", "og:locale", "en_US");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:url", "https://teamgrid.ai/");
    setMeta("name", "twitter:title", "TeamGrid - AI Workforce Intelligence Platform");
    setMeta("name", "twitter:description", "Detect issues early. Compare performance with AI context. Get actionable workforce insights in minutes.");
    setMeta("name", "twitter:image", ogImage);

    let scriptEl = document.getElementById("landing-jsonld");
    if (scriptEl) scriptEl.remove();
    scriptEl = document.createElement("script");
    scriptEl.id = "landing-jsonld";
    scriptEl.type = "application/ld+json";
    scriptEl.textContent = JSON.stringify(structuredData);
    document.head.appendChild(scriptEl);
    return () => {
      scriptEl = document.getElementById("landing-jsonld");
      if (scriptEl) scriptEl.remove();
    };
  }, [ogImage, isProduction, structuredData]);

  if (showAlternateLanding) {
    return (
      <AlternateLanding onSwitchBack={() => setShowAlternateLanding(false)} />
    );
  }

  return (
    <div className="min-h-screen bg-white text-[#232323] overflow-x-hidden overflow-y-visible">
      {/* Navigation */}
      <PublicHeader />

      {/* Main Content - hero section at top */}
      <div id="landing-hero" className="relative pt-20 sm:pt-24 md:pt-[140px] lg:pt-[160px] pb-0 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 bg-white overflow-visible">
        {/* Gradient Background */}
        <div
          className="absolute inset-0 w-full h-[600px] sm:h-[700px] md:h-[800px] lg:h-[893px] top-0 left-0 z-0"
          style={{
            background:
              "radial-gradient(88.35% 188.48% at 20% 20%, rgba(40, 180, 174, 0.05) 0%, rgba(40, 180, 174, 0) 50%), radial-gradient(88.35% 188.48% at 80% 80%, rgba(224, 204, 242, 0.05) 0%, rgba(224, 204, 242, 0) 50%)",
          }}
        ></div>
        {/* Mask Gradient */}
        <div
          className="absolute inset-0 w-full h-[600px] sm:h-[700px] md:h-[800px] lg:h-[893px] top-0 left-0 z-[1]"
          style={{
            background:
              "radial-gradient(60% 50% at 50% 30%, #FFFFFF 20%, rgba(255, 255, 255, 0) 70%)",
          }}
        ></div>
        <div className="max-w-[1152px] mx-auto relative z-[2]">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-stretch gap-8 md:gap-10 lg:gap-10 xl:gap-[60px]">
            {/* Left Side - Hero Section */}
            <motion.div
              variants={heroVariants}
              initial="hidden"
              animate="visible"
              className="flex flex-col items-start gap-6 w-full max-w-[480px] md:max-w-[500px] lg:max-w-[520px] xl:max-w-[580px]"
            >
              {/* Eyebrow Badge */}
              {/* <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2"
                style={{
                  background: "#232323",
                  border: "1px solid #333333",
                }}
              >
                <div className="w-4 h-4 rounded-full bg-[#28B4AE] flex items-center justify-center">
                  <Check className="w-3 h-3 text-black" strokeWidth={3} />
                </div>
                <span className="text-[13px] md:text-[14px] leading-[20px] font-semibold text-white">
                  Workforce Intelligence Platform
                </span>
              </motion.div> */}

              {/* Main Heading */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="w-full"
              >
                <h1 className="text-[28px] sm:text-[32px] md:text-[36px] lg:text-[42px] xl:text-[48px] leading-[1.1] font-bold text-[#232323] tracking-[-2px]">
                  AI Workforce Intelligence
                  <br />
                  <span style={{ color: "#232323" }}>Platform</span>{" "}
                  <span
                    style={{
                      color: "#28B4AE",
                    }}
                  >
                    That Actually
                  </span>
                  <br />
                  <span
                    style={{
                      color: "#28B4AE",
                    }}
                  >
                    Tells You What's Wrong
                  </span>
                </h1>
              </motion.div>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-[16px] sm:text-[17px] md:text-[18px] leading-[1.6] w-full"
                style={{ color: "#555555" }}
              >
                Move beyond traditional employee monitoring software. Teamgrid
                is a privacy-first workforce intelligence platform that helps
                leaders track employee performance, uncover productivity trends,
                and make better decisions without micromanagement
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-4 w-full"
              >
                <motion.a
                  href="/register"
                  className="px-8 py-4 rounded-lg font-semibold text-[16px] leading-[24px] text-white flex items-center justify-center gap-2 whitespace-nowrap"
                  style={{
                    background: "#28B4AE",
                    boxShadow: "0px 4px 20px rgba(40, 180, 174, 0.4)",
                  }}
                  variants={buttonHoverVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <Clock className="w-5 h-5 text-black flex-shrink-0" />
                  {/* <span className="text-black">Start Free 5-Day Trial</span> */}
                  <span className="text-black">Register</span>
                </motion.a>
                <motion.a
                  // href="https://youtu.be/H2qiGOXlwG8?si=NPwGnFTy-f1-rVMw"
                  href="https://youtu.be/H2qiGOXlwG8?si=NPwGnFTy-f1-rVMw"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="landing-cta-dark px-8 py-4 rounded-lg font-semibold text-[16px] leading-[24px] text-white bg-[#232323] border border-[#232323] hover:border-[#28B4AE]/50 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                  variants={buttonHoverVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <Play className="w-5 h-5 text-white flex-shrink-0" />
                  <span className="text-white">See Demo</span>
                </motion.a>
              </motion.div>

              {/* Additional Info */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="text-sm flex items-center gap-2 w-full"
                style={{ color: "#777777" }}
              >
                <span>
                  No credit card required • Full feature access • 5-minute setup
                </span>
              </motion.p>

              {/* Proof Statistics */}
              {/* <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="flex flex-col sm:flex-row items-start gap-6 sm:gap-8 md:gap-10 pt-6"
              >
                {[
                  {
                    number: "2.4M+",
                    label: "Hours Analyzed",
                    index: 0,
                  },
                  {
                    number: "38%",
                    label: "Avg. Productivity Gain",
                    index: 1,
                  },
                  {
                    number: "4.9",
                    label: "User Rating",
                    icon: "★",
                    index: 2,
                  },
                ].map((stat, idx) => (
                  <motion.div
                    key={stat.index}
                    variants={statsVariants}
                    custom={stat.index}
                    className="flex flex-col gap-1 relative"
                  >
                    {idx > 0 && (
                      <div
                        className="hidden sm:block absolute left-[-24px] top-1/2 transform -translate-y-1/2 w-[1px] h-12"
                        style={{
                          background:
                            "linear-gradient(180deg, rgba(51, 51, 51, 0) 0%, #333333 50%, rgba(51, 51, 51, 0) 100%)",
                        }}
                      ></div>
                    )}
                    <span
                      className="text-[28px] sm:text-[32px] md:text-[36px] leading-[36px] sm:leading-[40px] md:leading-[44px] font-extrabold text-white"
                    >
                      {stat.number}
                      {stat.icon && <span className="ml-1 text-[#28B4AE]">{stat.icon}</span>}
                    </span>
                    <p className="text-[14px] leading-[20px] text-gray-500">
                      {stat.label}
                    </p>
                  </motion.div>
                ))}
              </motion.div> */}
            </motion.div>

            {/* Right Side - Ask TeamGrid AI */}
            <div className="relative w-full lg:flex-1 lg:max-w-[572px] flex items-stretch lg:justify-end h-full">
              {/* Ask TeamGrid AI Component */}
              <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1,
                }}
                transition={{
                  opacity: { duration: 0.5, delay: 0.6 },
                  scale: { duration: 0.5, delay: 0.6 },
                }}
                className="relative w-full max-w-[572px] h-full flex items-stretch"
              >
                <AskTeamGridAI />
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Social Proof Section */}
      <section
        className="px-4 sm:px-8 relative z-10 mt-[40px] md:mt-[90px]"
        style={{
          background: "#E0F2F1",
          paddingTop: "32px",
          paddingBottom: "36px",
          borderTop: "1px solid rgba(35, 35, 35, 0.1)",
          borderBottom: "1px solid rgba(35, 35, 35, 0.1)",
        }}
      >
        <div className="max-w-[1320px] mx-auto">
          {/* Headline */}
          <h2 className="text-center text-[24px] sm:text-[33px] leading-[1.3] sm:leading-[30.6px] font-normal text-black/50 max-w-[1061px] mx-auto tracking-normal px-4 sm:px-0">
            Workforce intelligence trusted by forward-thinking teams worldwide
          </h2>

          {/* Metrics Row */}
          <div className="landing-stats-row grid grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-4 sm:gap-4 max-w-[600px] mb-[40px] mt-[32px] mx-auto px-4 sm:px-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0 }}
              className="text-center py-2"
            >
              <div className="landing-stats-number text-[32px] leading-[51.2px] font-extrabold mb-2 tracking-normal">
                12M+
              </div>
              <div className="landing-stats-label text-xs">
                Work Hours Tracked
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-center py-2"
            >
              <div className="landing-stats-number text-[32px] leading-[51.2px] font-extrabold mb-2 tracking-normal">
                35%
              </div>
              <div className="landing-stats-label text-xs">
                Avg Productivity Boost
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-center py-2"
            >
              <div className="landing-stats-number text-[32px] leading-[51.2px] font-extrabold mb-2 tracking-normal">
                4.9*
              </div>
              <div className="landing-stats-label text-xs">User Rating</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-center py-2"
            >
              <div className="landing-stats-number text-[32px] leading-[51.2px] font-extrabold mb-2 tracking-normal">
                500+
              </div>
              <div className="landing-stats-label text-xs">Active Teams</div>
            </motion.div>
          </div>

          {/* Logos Row */}
          <div className="relative max-w-[1100px] mx-auto">
            <style>{`
    @keyframes scroll-left {
      0% {
        transform: translateX(0);
      }
      100% {
        transform: translateX(-50%);
      }
    }
    .logo-scroll {
      animation: scroll-left 30s linear infinite;
    }
    .logo-scroll:hover {
      animation-play-state: paused;
    }
  `}</style>
            <div className="flex overflow-hidden">
              <div className="flex logo-scroll gap-8 sm:gap-12 lg:gap-16 items-center whitespace-nowrap">
                {(() => {
                  const logos = [
                    {
                      src: "/Brand_gridd.webp",
                      alt: "BrandGrid",
                      label: "BrandGrid",
                    },
                    {
                      src: "/saeller_777.webp",
                      alt: "Seller 7",
                      label: "Seller 7",
                    },
                    {
                      src: "/ecomsellertoollogo_.webp",
                      alt: "Ecomsellertool",
                      label: "Ecomsellertool",
                    },
                    { src: "/Cord4_11111.webp", alt: "CORD4", label: "CORD4" },
                    {
                      src: "/grow8_1.webp",
                      alt: "grow 8",
                      label: "",
                    },
                  ];
                  // Duplicate logos twice for seamless loop
                  return [...logos, ...logos].map((logo, index) => (
                    <div
                      key={`logo-${index}`}
                      className="flex-shrink-0 flex items-center gap-2"
                    >
                      <div
                        key={`logo-${index}`}
                        className="flex-shrink-0 flex items-center justify-center"
                      >
                        <img
                          src={logo.src}
                          alt={logo.alt}
                          className="h-6 sm:h-7 md:h-8 object-contain opacity-100 transition-opacity duration-300"
                        />
                      </div>
                      {/* <span className="text-[#232323] font-medium text-sm sm:text-base whitespace-nowrap">
                        {logo.label}
                      </span> */}
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section
        id="features"
        className="relative px-4 sm:px-8 py-[40px] md:py-[103.5px] bg-white"
        style={{ position: "relative", zIndex: 1 }}
      >
        {/* <section id="features" className="relative py-[140px] px-8 bg-black" style={{ position: 'relative', zIndex: 1 }}> */}
        <div className="max-w-[1150px] mx-auto">
          {/* Section Header */}
          <div className="text-center max-w-[680px] mx-auto mb-8 md:mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center px-4 py-2 rounded-full mb-[15px]"
              style={{
                background: "rgba(0, 217, 217, 0.06)",
                border: "1px solid rgba(0, 217, 217, 0.15)",
                fontSize: "12px",
                fontWeight: 600,
                color: "#28B4AE",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              <span>Core Capabilities</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-white mb-5"
              style={{
                fontSize: "clamp(36px, 4vw, 52px)",
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: "-0.03em",
              }}
            >
              Intelligence That Drives Results
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-gray-400"
              style={{
                fontSize: "18px",
                lineHeight: 1.7,
              }}
            >
              Move beyond basic metrics. TeamGrid reveals the patterns and
              opportunities hiding in your team's work data.
            </motion.p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center gap-[10px] mb-14">
            {[
              { id: "intelligence", label: "Org Intelligence" },
              { id: "visibility", label: "Team Goals" },
              { id: "insights", label: "Work Summary" },
              { id: "privacy", label: "Ask TeamGrid" },
              { id: "team-mgmt", label: "Team Management" },
              { id: "projects", label: "Manage Projects" },
            ].map((tab) => {
              const isActive = activeValuePanel === tab.id;

              return (
                <motion.button
                  key={tab.id}
                  onClick={() => setActiveValuePanel(tab.id)}
                  className="transition-all font-semibold"
                  style={{
                    padding: "14px 28px",
                    background: "#FFFFFF",
                    border: "1px solid",
                    borderRadius: "12px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                    outline: "none",
                  }}
                  animate={{
                    borderColor: isActive ? "#4ee8cf" : "#dedede",
                    color: isActive ? "#232323" : "#5c5c5c",
                    boxShadow: isActive
                      ? "0 0 0 1px rgba(78, 232, 207, 0.25)"
                      : "none",
                  }}
                  whileHover={
                    !isActive
                      ? {
                          background: "#FFFFFF",
                          borderColor: "#cfcfcf",
                          color: "#5c5c5c",
                        }
                      : {}
                  }
                  whileTap={{ scale: 0.98 }}
                >
                  {tab.label}
                </motion.button>
              );
            })}
          </div>

          {/* Panels */}
          <AnimatePresence mode="wait">
            {activeValuePanel === "intelligence" && (
              <motion.div
                key="intelligence"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4 }}
                className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-16 items-start"
              >
                {/* Left Content */}
                <div className="flex flex-col">
                  <h3
                    className="text-white mb-5"
                    style={{
                      fontSize: "32px",
                      fontWeight: 700,
                      lineHeight: 1.2,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Organizational intelligence software.
                  </h3>
                  <p
                    className="text-white mb-9"
                    style={{
                      fontSize: "17px",
                      lineHeight: 1.7,
                      color: "#555555",
                    }}
                  >
                    Move beyond basic employee time tracking. TeamGrid uses AI
                    powered automatic tracking to reveal patterns and turn time
                    data into actionable productivity insights.
                  </p>
                  <div className="flex flex-col gap-[18px] mb-9">
                    {[
                      {
                        title: "Automatic, AI-driven tracking",
                        description:
                          "Reduce manual work with automatic time tracking software that accurately captures work patterns across teams.",
                      },
                      {
                        title: "Smart performance and productivity insights",
                        description:
                          "A time tracking and productivity monitoring tool that surfaces inefficiencies and workload gaps fast.",
                      },
                      {
                        title: "Built-in attendance intelligence",
                        description:
                          "AI-led automated attendance tracking and employee attendance monitoring for real-time visibility.",
                      },
                      {
                        title: "Designed for scale and flexibility",
                        description:
                          "An AI-powered time tracking software built to support any workflow from small teams to enterprises.",
                      },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-[14px]">
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ background: "rgba(0, 217, 217, 0.1)" }}
                        >
                          <Check className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-semibold text-lg mb-2">
                            {item.title}
                          </h3>
                          <p
                            className="leading-relaxed"
                            style={{
                              fontSize: "15px",
                              lineHeight: 1.5,
                              color: "#555555",
                            }}
                          >
                            {item.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 w-fit">
                    <motion.a
                      href="/book-demo"
                      className="inline-flex items-center justify-center bg-[#19C7C2] text-black px-8 py-4 rounded-lg font-semibold text-[16px] leading-[24px] transition-all duration-300 relative z-10"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      See it in action
                    </motion.a>
                  </div>
                  {/* <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl" style={{ background: 'rgba(0, 217, 217, 0.06)', border: '1px solid rgba(0, 217, 217, 0.15)' }}>
                    <span className="font-mono font-extrabold text-[#28B4AE]" style={{ fontSize: '28px' }}>85%</span>
                    <span className="text-gray-400" style={{ fontSize: '14px' }}>of issues caught proactively</span>
                  </div> */}
                </div>
                {/* Right Preview */}
                <div className="bg-white border border-white/10 rounded-[20px] overflow-hidden">
                  <div
                    className="flex items-center gap-[10px] px-5 py-4"
                    style={{
                      background: "#FFFFFF",
                      borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: "#FF5F57" }}
                    ></div>
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: "#FEBC2E" }}
                    ></div>
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: "#28C840" }}
                    ></div>
                    <div className="flex-1"></div>
                    <div className="flex items-center gap-2">
                      <span
                        className="px-4 py-[10px] text-[14px] leading-none font-semibold rounded-[12px] text-white"
                        style={{
                          background: "#18C8C2",
                          color: "#FFFFFF",
                          WebkitTextFillColor: "#FFFFFF",
                        }}
                      >
                        Analysis
                      </span>
                      <span className="px-4 py-[10px] text-[14px] leading-none font-semibold text-[#5c5c5c] rounded-[12px] border border-[#dedede] bg-white">
                        Recs
                      </span>
                    </div>
                  </div>
                  <div className="p-6 space-y-6 bg-white">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-2xl flex items-center justify-center font-bold"
                        style={{ background: "#F1F1F1", color: "#6B5DAF" }}
                      >
                        SR
                      </div>
                      <div>
                        <div className="text-[#111111] font-bold text-[16px]">
                          Sarah Rodriguez
                        </div>
                        <div className="text-[#475569] text-[13px]">
                          Product Manager • Last 7 Days
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-[#FF3D5A] text-base">⚠</span>
                        <span className="text-[#FF3D5A] font-bold uppercase tracking-[0.02em]">
                          Issues Detected
                        </span>
                      </div>
                      <div className="space-y-4">
                        <div className="bg-[#F8FAFC] border border-[#DCE6F2] rounded-2xl p-4">
                          <div className="text-[#FF3D5A] text-xs mb-1 font-bold uppercase">
                            High Priority
                          </div>
                          <div className="text-[#475569] text-sm leading-relaxed">
                            Meeting overload detected: Spending 45% more time in
                            meetings compared to team average, reducing deep
                            work time.
                          </div>
                        </div>
                        <div className="bg-[#F8FAFC] border border-[#DCE6F2] rounded-2xl p-4">
                          <div className="text-[#FF7B4B] text-xs mb-1 font-bold uppercase">
                            Medium Priority
                          </div>
                          <div className="text-[#475569] text-sm leading-relaxed">
                            Context switching pattern identified: Averaging 18
                            app switches per hour, 30% above optimal threshold.
                          </div>
                        </div>
                        <div className="bg-[#F8FAFC] border border-[#DCE6F2] rounded-2xl p-4">
                          <div className="text-[#EAC94D] text-xs mb-1 font-bold uppercase">
                            Low Priority
                          </div>
                          <div className="text-[#64748B] text-sm leading-relaxed">
                            Late afternoon productivity dip: 25% reduction in
                            output between 3-5 PM.
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-[#18C8C2] text-lg">✧</span>
                        <span className="text-[#18C8C2] font-bold">
                          AI Recommendation
                        </span>
                      </div>
                      <div className="bg-[#F8FAFC] border border-[#DCE6F2] rounded-2xl p-5">
                        <div className="text-[#475569] font-bold mb-2">
                          Schedule Optimization: Block 2-Hour Focus Windows
                        </div>
                        <div className="text-[#475569] text-sm leading-relaxed">
                          Creating dedicated focus blocks Tuesday & Thursday
                          mornings could increase deep work output by 35%.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {activeValuePanel === "visibility" && (
              <motion.div
                key="visibility"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4 }}
                className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-16 items-start"
              >
                <div className="flex flex-col relative z-10 overflow-visible">
                  <h3
                    className="text-white mb-5"
                    style={{
                      fontSize: "32px",
                      fontWeight: 700,
                      lineHeight: 1.2,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Team & Individual Goals
                  </h3>
                  <p
                    className="text-white mb-9"
                    style={{
                      fontSize: "17px",
                      lineHeight: 1.7,
                      color: "#555555",
                    }}
                  >
                    Set, track, and achieve goals with visual calendar tracking.
                    Monitor progress across team members, view goals set by
                    others, and stay aligned with organizational objectives.
                  </p>
                  <div className="flex flex-col gap-[18px] mb-9">
                    {[
                      {
                        title:
                          "Visual calendar view with daily goal indicators",
                        description:
                          "Interactive calendar interface that displays all team goals with clear visual indicators, making it easy to track progress at a glance.",
                      },
                      {
                        title:
                          "See goals set by other team members for transparency",
                        description:
                          "Full visibility into team objectives promotes alignment and helps everyone understand how their work contributes to shared goals.",
                      },
                      {
                        title:
                          'Quick goal creation with one-click "Add Goal" functionality',
                        description:
                          "Streamlined goal-setting process that lets you create and assign goals instantly, reducing administrative overhead.",
                      },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-[14px]">
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ background: "rgba(0, 217, 217, 0.1)" }}
                        >
                          <Check className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3
                            className="text-white text-lg mb-2"
                            style={{ color: "#555555" }}
                          >
                            {item.title}
                          </h3>
                          {/* <p className="leading-relaxed" style={{ fontSize: '15px', lineHeight: 1.5, color: '#555555' }}>
                            {item.description}
                          </p> */}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl mb-4 w-fit"
                    style={{
                      background: "#CFE6E8",
                      border: "1px solid #B8DEE1",
                    }}
                  >
                    <span
                      className="font-mono font-extrabold text-[#18C8C2]"
                      style={{ fontSize: "28px", lineHeight: "44.8px" }}
                    >
                      3.2x
                    </span>
                    <span
                      className="text-[#666666]"
                      style={{ fontSize: "14px", lineHeight: "22.4px" }}
                    >
                      higher goal completion rate
                    </span>
                  </div>
                  <div className="mt-6 w-fit">
                    <motion.a
                      href="/book-demo"
                      className="inline-flex items-center justify-center bg-[#19C7C2] text-black px-8 py-4 rounded-lg font-semibold text-[16px] leading-[24px] transition-all duration-300 relative z-10"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      See it in action
                    </motion.a>
                  </div>
                </div>
                <div className="bg-white border border-white/10 rounded-[20px] overflow-hidden relative z-0">
                  <div
                    className="flex items-center gap-[10px] px-5 py-4"
                    style={{
                      background: "#FFFFFF",
                      borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: "#FF5F57" }}
                    ></div>
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: "#FEBC2E" }}
                    ></div>
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: "#28C840" }}
                    ></div>
                    <div className="flex-1"></div>
                    <div className="flex items-center gap-2">
                      <span
                        className="px-4 py-[10px] text-[14px] leading-none font-semibold rounded-[12px] text-white"
                        style={{
                          background: "#18C8C2",
                          color: "#FFFFFF",
                          WebkitTextFillColor: "#FFFFFF",
                        }}
                      >
                        Goals
                      </span>
                      <span className="px-4 py-[10px] text-[14px] leading-none font-semibold text-[#5c5c5c] rounded-[12px] border border-[#dedede] bg-white">
                        Calendar
                      </span>
                    </div>
                  </div>
                  <div className="p-6 space-y-6 bg-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-base"
                          style={{ background: "#28B4AE", color: "#FFFFFF" }}
                        >
                          MJ
                        </div>
                        <div>
                          <div className="text-[#000000] font-semibold">
                            Michael Johnson
                          </div>
                          <div className="text-gray-400 text-sm">
                            Engineering Lead
                          </div>
                        </div>
                      </div>
                      <button className="px-5 py-2.5 bg-[#F3FFFC] border border-[#18C8C2] text-[#18C8C2] rounded-xl text-sm font-semibold transition-colors">
                        + Add Goal
                      </button>
                    </div>
                    <div className="text-[#111111] font-bold text-lg mb-1">
                      Team Goals
                    </div>
                    <div className="grid grid-cols-6 gap-2 mb-6">
                      {["MON", "TUE", "WED", "THU", "FRI", "SAT"].map(
                        (day, idx) => (
                          <div
                            key={idx}
                            className={`text-center rounded-2xl border ${
                              idx === 4
                                ? "bg-[#18C8C2] border-[#18C8C2] shadow-[0_10px_20px_rgba(24,200,194,0.24)] px-2 py-3"
                                : "bg-[#F8FAFC] border-[#DCE6F2] px-2 py-3"
                            }`}
                          >
                            <div
                              className={`${
                                idx === 4
                                  ? "text-white/90 text-[11px] mb-1 font-medium"
                                  : "text-[11px] text-[#8B8B8B] mb-1 font-medium"
                              } uppercase`}
                            >
                              {day}
                            </div>
                            <div
                              className={`${
                                idx === 4
                                  ? "font-bold text-sm leading-none"
                                  : "text-[#475569] font-semibold text-sm leading-none"
                              }`}
                              style={
                                idx === 4 ? { color: "#FFFFFF" } : undefined
                              }
                            >
                              {20 + idx}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                    <div className="space-y-3">
                      <div className="bg-[#F8FAFC] border border-[#DCE6F2] rounded-2xl p-4">
                        <div className="flex justify-between mb-2">
                          <div className="text-[#111111] font-bold text-[16px]">
                            Complete Q1 Product Launch
                          </div>
                          <div className="text-[#18C8C2] font-bold text-[16px]">
                            75%
                          </div>
                        </div>
                        <div className="w-full bg-[#E5ECF5] rounded-full h-2.5 mb-2">
                          <div
                            className="bg-[#18C8C2] h-2.5 rounded-full"
                            style={{ width: "75%" }}
                          ></div>
                        </div>
                        <div className="flex justify-between text-xs text-[#9A9A9A]">
                          <span>Due: Jan 31</span>
                          <span>3 team members</span>
                        </div>
                      </div>
                      <div className="bg-[#F8FAFC] border border-[#DCE6F2] rounded-2xl p-4">
                        <div className="flex justify-between mb-2">
                          <div className="text-[#111111] font-bold text-[16px]">
                            Improve Code Coverage to 85%
                          </div>
                          <div className="text-[#18C8C2] font-bold text-[16px]">
                            62%
                          </div>
                        </div>
                        <div className="w-full bg-[#E5ECF5] rounded-full h-2.5 mb-2">
                          <div
                            className="bg-[#18C8C2] h-2.5 rounded-full"
                            style={{ width: "62%" }}
                          ></div>
                        </div>
                        <div className="flex justify-between text-xs text-[#9A9A9A]">
                          <span>Due: Feb 15</span>
                          <span>5 team members</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {activeValuePanel === "insights" && (
              <motion.div
                key="insights"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4 }}
                className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-16 items-start"
              >
                <div className="flex flex-col">
                  <h3
                    className="text-white mb-5"
                    style={{
                      fontSize: "32px",
                      fontWeight: 700,
                      lineHeight: 1.2,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Automatic work summaries
                  </h3>
                  <p
                    className="text-white mb-9"
                    style={{
                      fontSize: "17px",
                      lineHeight: 1.7,
                      color: "#555555",
                    }}
                  >
                    Comprehensive, automatically generated summaries of team
                    member activities. Get detailed breakdowns of what work was
                    done, session analysis, and cached summaries for instant
                    access.
                  </p>
                  <div className="flex flex-col gap-[18px] mb-9">
                    {[
                      {
                        title:
                          "Daily and hourly insights with time-stamped sessions",
                        description:
                          "Comprehensive activity summaries broken down by time periods, providing detailed visibility into work patterns and productivity trends.",
                      },
                      {
                        title:
                          'Detailed "What Work Was Done" breakdowns by activity',
                        description:
                          "AI-generated summaries that categorize and explain all work activities, giving you clear insights into team member contributions.",
                      },
                      {
                        title:
                          "Cached summaries with regeneration option for fresh insights",
                        description:
                          "Instant access to previously generated summaries with the ability to regenerate for updated analysis and fresh perspectives.",
                      },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-[14px]">
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ background: "rgba(0, 217, 217, 0.1)" }}
                        >
                          <Check className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3
                            className="text-white  text-lg mb-2"
                            style={{ color: "#555555" }}
                          >
                            {item.title}
                          </h3>
                          {/* <p className="leading-relaxed" style={{ fontSize: '15px', lineHeight: 1.5, color: '#555555' }}>
                            {item.description}
                          </p> */}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl mb-4 w-fit"
                    style={{
                      background: "#CFE6E8",
                      border: "1px solid #B8DEE1",
                    }}
                  >
                    <span
                      className="font-mono font-extrabold text-[#18C8C2]"
                      style={{ fontSize: "28px", lineHeight: "44.8px" }}
                    >
                      4200+
                    </span>
                    <span
                      className="text-[#666666]"
                      style={{ fontSize: "14px", lineHeight: "22.4px" }}
                    >
                      characters per summary
                    </span>
                  </div>
                  <div className="mt-6 w-fit">
                    <motion.a
                      href="/book-demo"
                      className="inline-flex items-center justify-center bg-[#19C7C2] text-black px-8 py-4 rounded-lg font-semibold text-[16px] leading-[24px] transition-all duration-300 relative z-10"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      See it in action
                    </motion.a>
                  </div>
                </div>
                <div className="bg-white border border-white/10 rounded-[20px] overflow-hidden">
                  <div
                    className="flex items-center gap-[10px] px-5 py-4"
                    style={{
                      background: "#FFFFFF",
                      borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: "#FF5F57" }}
                    ></div>
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: "#FEBC2E" }}
                    ></div>
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: "#28C840" }}
                    ></div>
                    <div className="flex-1"></div>
                    <div className="flex items-center gap-2">
                      <span
                        className="px-4 py-[10px] text-[14px] leading-none font-semibold rounded-[12px] text-white"
                        style={{
                          background: "#18C8C2",
                          color: "#FFFFFF",
                          WebkitTextFillColor: "#FFFFFF",
                        }}
                      >
                        Daily Summary
                      </span>
                      <span className="px-4 py-[10px] text-[14px] leading-none font-semibold text-[#5c5c5c] rounded-[12px] border border-[#dedede] bg-white">
                        Hourly Insights
                      </span>
                    </div>
                  </div>
                  <div className="p-6 space-y-6 bg-white">
                    <div className="flex items-center justify-between pb-5 border-b border-[#E5EEF8]">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-base"
                          style={{ background: "#28B4AE", color: "#FFFFFF" }}
                        >
                          AK
                        </div>
                        <div>
                          <div className="text-[#111111] font-bold">
                            Alex Chen
                          </div>
                          <div className="text-[#475569] text-sm">
                            Senior Software Engineer
                          </div>
                        </div>
                      </div>
                      <div className="px-4 py-2 text-sm leading-none bg-[#DDF3EE] text-[#29A65A] rounded-xl font-semibold">
                        Cached
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-[#28B4AE] font-semibold">
                          What Work Was Done
                        </span>
                      </div>
                      <div className="text-gray-300 text-sm leading-relaxed mb-4">
                        Today Alex completed substantial progress on the API
                        integration project, dedicating 6.5 hours to development
                        work. Key achievements include implementing three new
                        REST endpoints, refactoring the authentication
                        middleware for improved security, and conducting
                        comprehensive code reviews for the team's pull requests.
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span>🕐</span>
                          <span className="text-white font-semibold text-sm">
                            Morning Focus Session (9:00 AM - 12:30 PM)
                          </span>
                        </div>
                        <div className="text-gray-300 text-xs leading-relaxed">
                          Deep work on backend development with minimal
                          interruptions. Implemented core API endpoints and
                          completed unit testing. Productivity score: 94% during
                          this period.
                        </div>
                      </div>
                      <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span>🕑</span>
                          <span className="text-white font-semibold text-sm">
                            Afternoon Collaboration (2:00 PM - 5:30 PM)
                          </span>
                        </div>
                        <div className="text-gray-300 text-xs leading-relaxed">
                          Code reviews, team sync meetings, and pair programming
                          session. Provided technical guidance to junior
                          developers and resolved blocking issues for ongoing
                          sprints.
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-center pt-2">
                      <span className="inline-flex items-center px-6 py-2 rounded-[16px] bg-[#EAF1F8] text-[#8B8B8B] text-sm">
                        3847 characters analyzed
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {activeValuePanel === "privacy" && (
              <motion.div
                key="privacy"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4 }}
                className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-16 items-start"
              >
                <div className="flex flex-col">
                  <h3
                    className="text-white mb-5"
                    style={{
                      fontSize: "32px",
                      fontWeight: 700,
                      lineHeight: 1.2,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    AI assistant for team management
                  </h3>
                  <p
                    className="text-white mb-9"
                    style={{
                      fontSize: "17px",
                      lineHeight: 1.7,
                      color: "#555555",
                    }}
                  >
                    Ask anything about your team's productivity and work
                    patterns in natural language. Get instant answers with
                    suggested prompts for common queries and voice input
                    support.
                  </p>
                  <div className="flex flex-col gap-[18px] mb-9">
                    {[
                      {
                        title:
                          "Conversational interface with smart prompt suggestions",
                        description:
                          "Natural language interface that understands your questions and provides intelligent prompt suggestions to help you get the insights you need.",
                      },
                      {
                        title: "Voice input capability for hands-free queries",
                        description:
                          "Ask questions using voice commands for convenient, hands-free access to team productivity data and insights.",
                      },
                      {
                        title:
                          "Pre-configured prompts: productivity, top performers, improvements",
                        description:
                          "Quick access to common queries with ready-made prompts that help you quickly analyze productivity, identify top performers, and discover improvement opportunities.",
                      },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-[14px]">
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ background: "rgba(0, 217, 217, 0.1)" }}
                        >
                          <Check className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3
                            className="text-white  text-lg mb-2"
                            style={{ color: "#555555" }}
                          >
                            {item.title}
                          </h3>
                          {/* <p className="leading-relaxed" style={{ fontSize: '15px', lineHeight: 1.5, color: '#555555' }}>
                            {item.description}
                          </p> */}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl mb-4 w-fit"
                    style={{
                      background: "#CFE6E8",
                      border: "1px solid #B8DEE1",
                    }}
                  >
                    <span
                      className="font-mono font-extrabold text-[#18C8C2]"
                      style={{ fontSize: "28px", lineHeight: "44.8px" }}
                    >
                      &lt; 3s
                    </span>
                    <span
                      className="text-[#666666]"
                      style={{ fontSize: "14px", lineHeight: "22.4px" }}
                    >
                      average response time
                    </span>
                  </div>
                  <div className="mt-6 w-fit">
                    <motion.a
                      href="/book-demo"
                      className="inline-flex items-center justify-center bg-[#19C7C2] text-black px-8 py-4 rounded-lg font-semibold text-[16px] leading-[24px] transition-all duration-300 relative z-10"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      See it in action
                    </motion.a>
                  </div>
                </div>
                <div className="bg-white border border-white/10 rounded-[20px] overflow-hidden">
                  <div
                    className="flex items-center gap-[10px] px-5 py-4"
                    style={{
                      background: "#FFFFFF",
                      borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: "#FF5F57" }}
                    ></div>
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: "#FEBC2E" }}
                    ></div>
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: "#28C840" }}
                    ></div>
                    <div className="flex-1"></div>
                    <div className="flex items-center gap-2">
                      <span
                        className="px-4 py-[10px] text-[14px] leading-none font-semibold rounded-[12px] text-white"
                        style={{
                          background: "#18C8C2",
                          color: "#FFFFFF",
                          WebkitTextFillColor: "#FFFFFF",
                        }}
                      >
                        Chat
                      </span>
                      <span className="px-4 py-[10px] text-[14px] leading-none font-semibold text-[#5c5c5c] rounded-[12px] border border-[#dedede] bg-white">
                        History
                      </span>
                    </div>
                  </div>
                  <div className="p-6 space-y-6 bg-white">
                    <div className="text-center py-8">
                      <div className="text-white font-semibold text-lg mb-2">
                        How can I help you today?
                      </div>
                      <div className="text-gray-400 text-sm">
                        Ask me anything about your team's productivity and work
                        patterns
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        {
                          icon: "📊",
                          text: "Show me this week's team productivity trends",
                        },
                        {
                          icon: "🏆",
                          text: "Who are the top performers this month?",
                        },
                        {
                          icon: "⚠️",
                          text: "Which projects need immediate attention?",
                        },
                        {
                          icon: "💡",
                          text: "How can we optimize meeting schedules?",
                        },
                      ].map((prompt, idx) => (
                        <div
                          key={idx}
                          className="bg-white/5 border border-white/10 rounded-lg p-3 hover:bg-white/10 transition-colors cursor-pointer"
                        >
                          <div className="text-2xl mb-2">{prompt.icon}</div>
                          <div className="text-gray-300 text-xs leading-relaxed">
                            {prompt.text}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg p-3">
                      <input
                        type="text"
                        placeholder="Type your question..."
                        className="flex-1 bg-transparent text-white text-sm outline-none"
                        disabled
                      />
                      <button className="text-gray-400 hover:text-white text-lg">
                        🎤
                      </button>
                      <button className="text-gray-400 hover:text-white text-lg">
                        →
                      </button>
                    </div>
                    <div className="text-center text-gray-500 text-xs">
                      Press Shift + Enter for new line
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {activeValuePanel === "team-mgmt" && (
              <motion.div
                key="team-mgmt"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4 }}
                className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-16 items-start"
              >
                <div className="flex flex-col">
                  <h3
                    className="text-white mb-5"
                    style={{
                      fontSize: "32px",
                      fontWeight: 700,
                      lineHeight: 1.2,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    AI-powered team analytics
                  </h3>
                  <p
                    className="text-white mb-9"
                    style={{
                      fontSize: "17px",
                      lineHeight: 1.7,
                      color: "#555555",
                    }}
                  >
                    Manage your entire team structure with hierarchical
                    reporting, role-based permissions, and organizational trees.
                    Control access, track team size, and visualize reporting
                    relationships.
                  </p>
                  <div className="flex flex-col gap-[18px] mb-9">
                    {[
                      {
                        title:
                          "Hierarchical org structure with reporting managers",
                        description:
                          "Build and manage complex organizational hierarchies with clear reporting relationships and manager assignments.",
                      },
                      {
                        title:
                          "Granular permissions: Add Project, View Reports, Edit Profile, View Activity",
                        description:
                          "Fine-grained access control that lets you customize permissions for each team member, ensuring the right people have the right access.",
                      },
                      {
                        title: "Position-based roles and optional stealth mode",
                        description:
                          "Assign roles based on positions and enable stealth mode for sensitive tracking scenarios while maintaining full organizational visibility.",
                      },
                      {
                        title:
                          "Visual organizational tree showing direct and indirect reports",
                        description:
                          "Interactive org chart visualization that displays all reporting relationships, making it easy to understand team structure at a glance.",
                      },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-[14px]">
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ background: "rgba(0, 217, 217, 0.1)" }}
                        >
                          <Check className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3
                            className="text-white  text-lg mb-2"
                            style={{ color: "#555555" }}
                          >
                            {item.title}
                          </h3>
                          {/* <p className="leading-relaxed" style={{ fontSize: '15px', lineHeight: 1.5, color: '#555555' }}>
                            {item.description}
                          </p> */}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div
                    className="inline-flex items-center gap-3 px-5 py-3 rounded-xl mb-6"
                    style={{
                      background: "#CFE6E8",
                      border: "1px solid #B8DEE1",
                    }}
                  >
                    <span
                      className="font-mono font-extrabold text-[#57C8C5]"
                      style={{ fontSize: "28px" }}
                    >
                      8
                    </span>
                    <span
                      className="text-[#666B75]"
                      style={{ fontSize: "14px" }}
                    >
                      billing-enabled positions
                    </span>
                  </div>
                  <div className="mt-6 w-fit">
                    <motion.a
                      href="/book-demo"
                      className="inline-flex items-center justify-center bg-[#19C7C2] text-black px-8 py-4 rounded-lg font-semibold text-[16px] leading-[24px] transition-all duration-300 relative z-10"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      See it in action
                    </motion.a>
                  </div>
                </div>
                <div className="bg-white border border-white/10 rounded-[20px] overflow-hidden">
                  <div
                    className="flex items-center gap-[10px] px-5 py-4"
                    style={{
                      background: "#FFFFFF",
                      borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: "#FF5F57" }}
                    ></div>
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: "#FEBC2E" }}
                    ></div>
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: "#28C840" }}
                    ></div>
                    <div className="flex-1"></div>
                    <div className="flex items-center gap-2">
                      <span
                        className="px-4 py-[10px] text-[14px] leading-none font-semibold rounded-[12px] text-white"
                        style={{
                          background: "#18C8C2",
                          color: "#FFFFFF",
                          WebkitTextFillColor: "#FFFFFF",
                        }}
                      >
                        Team
                      </span>
                      <span className="px-4 py-[10px] text-[14px] leading-none font-semibold text-[#5c5c5c] rounded-[12px] border border-[#dedede] bg-white">
                        Org Tree
                      </span>
                    </div>
                  </div>
                  <div className="p-6 space-y-4 bg-white">
                    {[
                      {
                        name: "Jennifer Wilson",
                        role: "VP of Engineering",
                        avatar: "JW",
                        avatarBg: "#28B4AE",
                        avatarColor: "#FFFFFF",
                        roleType: "Manager",
                        permissions: "Full Access",
                        status: "Activated",
                        reports: 12,
                        roleBadge: "ceo",
                      },
                      {
                        name: "Marcus Chen",
                        role: "Senior Product Designer",
                        avatar: "MC",
                        avatarBg: "#F1F1F1",
                        avatarColor: "#6B5DAF",
                        roleType: "Team Member",
                        permissions: "4 Permissions",
                        reportsTo: "Jennifer Wilson",
                        projects: 3,
                        roleBadge: "exec",
                      },
                      {
                        name: "Lisa Martinez",
                        role: "Lead Data Analyst",
                        avatar: "LM",
                        avatarBg: "#D6F0EF",
                        avatarColor: "#2FA35A",
                        roleType: "Team Member",
                        permissions: "5 Permissions",
                        reportsTo: "Jennifer Wilson",
                        projects: 2,
                        roleBadge: "exec",
                      },
                    ].map((member, idx) => (
                      <div
                        key={idx}
                        className="bg-[#F8FAFC] border border-[#DCE6F2] rounded-xl p-5"
                      >
                        <div className="flex items-center gap-3.5 mb-4">
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-base"
                            style={{
                              background: member.avatarBg,
                              color: member.avatarColor || "#000000",
                            }}
                          >
                            {member.avatar}
                          </div>
                          <div className="flex-1">
                            <div className="text-[#111111] font-bold text-[18px] mb-1">
                              {member.name}
                            </div>
                            <div
                              className={`inline-block px-2.5 py-1 rounded-md text-[11px] font-semibold ${
                                member.roleBadge === "ceo"
                                  ? "bg-[#ECE9F8] text-[#6B5DAF]"
                                  : member.name === "Marcus Chen"
                                    ? "bg-[#EAF6EC] text-[#2EA44F]"
                                    : "bg-[#E5F6F5] text-[#16B7B1]"
                              }`}
                            >
                              {member.role}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-[14px]">
                          <div className="text-[#475569]">
                            <span className="text-[#475569]">Role:</span>{" "}
                            {member.roleType}
                          </div>
                          <div className="text-[#475569]">
                            <span className="text-[#475569]">
                              {member.reportsTo
                                ? "Reports to:"
                                : "Permissions:"}
                            </span>{" "}
                            {member.reportsTo || member.permissions}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {activeValuePanel === "projects" && (
              <motion.div
                key="projects"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4 }}
                className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-16 items-start"
              >
                <div className="flex flex-col">
                  <h3
                    className="text-white mb-5"
                    style={{
                      fontSize: "32px",
                      fontWeight: 700,
                      lineHeight: 1.2,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Project & Assignment Management
                  </h3>
                  <p
                    className="text-white mb-9"
                    style={{
                      fontSize: "17px",
                      lineHeight: 1.7,
                      color: "#555555",
                    }}
                  >
                    Create, organize, and track all your projects in one place.
                    Manage team assignments, monitor active projects, and
                    control project status with comprehensive team management
                    capabilities.
                  </p>
                  <div className="flex flex-col gap-[18px] mb-9">
                    {[
                      {
                        title:
                          "Project cards with descriptions and team size tracking",
                        description:
                          "Visual project cards that display key information including descriptions and real-time team member counts for each project.",
                      },
                      {
                        title:
                          "Active/Archive toggle for project lifecycle management",
                        description:
                          "Easily manage project status with a simple toggle to move projects between active and archived states, keeping your workspace organized.",
                      },
                      {
                        title:
                          "Quick actions: Analytics, Edit, Delete for each project",
                        description:
                          "Streamlined project management with quick access to analytics, editing, and deletion options directly from each project card.",
                      },
                      {
                        title: "Search and filter by name or description",
                        description:
                          "Powerful search functionality that helps you quickly find projects by searching through names or descriptions.",
                      },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-[14px]">
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ background: "rgba(0, 217, 217, 0.1)" }}
                        >
                          <Check className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3
                            className="text-white  text-lg mb-2"
                            style={{ color: "#555555" }}
                          >
                            {item.title}
                          </h3>
                          {/* <p className="leading-relaxed" style={{ fontSize: '15px', lineHeight: 1.5, color: '#555555' }}>
                            {item.description}
                          </p> */}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl mb-4 w-fit"
                    style={{
                      background: "#CFE6E8",
                      border: "1px solid #B8DEE1",
                    }}
                  >
                    <span
                      className="font-mono font-extrabold text-[#18C8C2]"
                      style={{ fontSize: "28px", lineHeight: "44.8px" }}
                    >
                      11
                    </span>
                    <span
                      className="text-[#666666]"
                      style={{ fontSize: "14px", lineHeight: "22.4px" }}
                    >
                      active projects tracked
                    </span>
                  </div>
                  <div className="mt-6 w-fit">
                    <motion.a
                      href="/book-demo"
                      className="inline-flex items-center justify-center bg-[#19C7C2] text-black px-8 py-4 rounded-lg font-semibold text-[16px] leading-[24px] transition-all duration-300 relative z-10"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      See it in action
                    </motion.a>
                  </div>
                </div>
                <div className="bg-white border border-white/10 rounded-[20px] overflow-hidden">
                  <div
                    className="flex items-center gap-[10px] px-5 py-4"
                    style={{
                      background: "#FFFFFF",
                      borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: "#FF5F57" }}
                    ></div>
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: "#FEBC2E" }}
                    ></div>
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: "#28C840" }}
                    ></div>
                    <div className="flex-1"></div>
                    <div className="flex items-center gap-2">
                      <span
                        className="px-4 py-[10px] text-[14px] leading-none font-semibold rounded-[12px] text-white"
                        style={{
                          background: "#18C8C2",
                          color: "#FFFFFF",
                          WebkitTextFillColor: "#FFFFFF",
                        }}
                      >
                        All Projects
                      </span>
                      <span className="px-4 py-[10px] text-[14px] leading-none font-semibold text-[#5c5c5c] rounded-[12px] border border-[#dedede] bg-white">
                        Active
                      </span>
                    </div>
                  </div>
                  <div className="p-6 bg-white">
                    <div className="grid grid-cols-1 gap-4">
                      {[
                        {
                          name: "Q1 Mobile App Launch",
                          desc: "Cross-platform mobile application development with focus on iOS and Android deployment for Q1 release...",
                          members: 8,
                        },
                        {
                          name: "Dashboard Redesign",
                          desc: "Complete UI/UX overhaul of the main analytics dashboard with improved data visualization and user experience...",
                          members: 5,
                        },
                        {
                          name: "API Integration",
                          desc: "Building scalable REST API infrastructure to support third-party integrations and webhook functionality...",
                          members: 6,
                        },
                        {
                          name: "Marketing Automation",
                          desc: "Implementing automated email campaigns, lead scoring, and customer journey tracking for marketing team...",
                          members: 4,
                        },
                      ]
                        .slice(0, 3)
                        .map((project, idx) => (
                          <div
                            key={idx}
                            className="bg-[#F8FAFC] border border-[#DCE6F2] rounded-2xl p-5"
                          >
                            <div className="flex justify-between items-start mb-3">
                              <div className="text-[#111111] font-bold text-[14px] flex-1 pr-2">
                                {project.name}
                              </div>
                              <div className="px-3 py-1 bg-[#DDF3EE] text-[#29A65A] text-xs font-semibold rounded-lg uppercase whitespace-nowrap">
                                Active
                              </div>
                            </div>
                            <div className="text-[#475569] text-[12px] mb-4 leading-relaxed">
                              {project.desc}
                            </div>
                            <div className="flex items-center justify-end">
                              <span className="text-[#8B8B8B] text-xs mr-1.5">
                                👥
                              </span>
                              <span className="text-[#8B8B8B] text-xs">
                                {project.members} members
                              </span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Traditional Monitoring Section */}
      <section className="relative py-[57.5px] px-4 sm:px-6 md:px-8 lg:px-12 bg-white">
        <div className="max-w-[1150px] mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center px-4 py-2 rounded-full mb-[15px]"
              style={{
                background: "rgba(0, 217, 217, 0.06)",
                border: "1px solid rgba(0, 217, 217, 0.15)",
                fontSize: "12px",
                fontWeight: 600,
                color: "#28B4AE",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              <span>THE PROBLEM</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-[28px] sm:text-[32px] md:text-[36px] lg:text-[42px] font-bold text-white leading-tight max-w-[900px] mx-auto text-center"
            >
              Traditional Monitoring Breeds Distrust.
              <br />
              You Need Operational Intelligence.
            </motion.h2>
          </div>

          {/* Three Columns */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {/* Column 1: The Problem */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="rounded-xl p-6 text-center"
              style={{ backgroundColor: "#FEE7ED" }}
            >
              <div className="flex justify-center mb-4">
                <div
                  className="w-20 h-20 rounded-lg flex items-center justify-center"
                  style={{
                    backgroundColor: "#FEECEE",
                    backgroundImage: 'url("/Overlay.webp")',
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "100% 100%",
                  }}
                ></div>
              </div>
              <h3 className="text-[#000000] font-bold text-xl mb-3">
                The Problem
              </h3>
              <p className="text-[#000000] text-sm mb-6">
                Traditional tools track screenshots and mouse clicks
              </p>
              <ul className="space-y-3 flex flex-col items-center">
                {[
                  "Screenshot surveillance",
                  "Mouse/keyboard tracking",
                  "GPS location monitoring",
                  "Creates trust issues",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <X className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <span className="text-[#000000] text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Column 2: The Impact */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="rounded-xl p-6 text-center"
              style={{ backgroundColor: "#FEF0E7" }}
            >
              <div className="flex justify-center mb-4">
                <div
                  className="w-20 h-20 rounded-lg flex items-center justify-center relative"
                  style={{
                    backgroundColor: "#FFF5F2",
                    backgroundImage: 'url("/Overlay_2.webp")',
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "100% 100%",
                  }}
                ></div>
              </div>
              <h3 className="text-[#000000] font-bold text-xl mb-3">
                The Impact
              </h3>
              <p className="text-[#000000] text-sm mb-6">
                Drowning in dashboards but still don't know:
              </p>
              <ul className="space-y-3 flex flex-col items-center">
                {[
                  "Who's blocked vs. just busy?",
                  "Where manual work wastes time?",
                  "Which systems cause delays?",
                  "What to fix first?",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <X className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <span className="text-[#000000] text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Column 3: The Solution */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="rounded-xl p-6 text-center"
              style={{ backgroundColor: "#E7FEF0" }}
            >
              <div className="flex justify-center mb-4">
                <div
                  className="w-20 h-20 rounded-lg flex items-center justify-center"
                  style={{
                    backgroundColor: "#E0F2F1",
                    backgroundImage: 'url("/Overlay3.webp")',
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "100% 100%",
                  }}
                ></div>
              </div>
              <h3 className="text-[#000000] font-bold text-xl mb-3">
                The Solution
              </h3>
              <p className="text-[#000000] text-sm mb-6">
                AI that answers questions in seconds
              </p>
              <ul className="space-y-3 flex flex-col items-center">
                {[
                  "Detects patterns tools miss",
                  "Recommends specific actions",
                  "Respects privacy",
                  "Delivers clarity instantly",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-[#000000] text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Compare Team Members Section - background ends at teal box with a little padding below */}
      <section className="relative pt-[57.5px] pb-12 sm:pb-14 md:pb-16 px-4 sm:px-6 md:px-8 lg:px-12 bg-[#F3F4F6]">
        <div className="max-w-[1150px] mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center px-4 py-2 rounded-full mb-[15px]"
              style={{
                background: "rgba(0, 217, 217, 0.06)",
                border: "1px solid rgba(0, 217, 217, 0.15)",
                fontSize: "12px",
                fontWeight: 600,
                color: "#28B4AE",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              <span>TEAM COMPARISON</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-[28px] sm:text-[32px] md:text-[36px] lg:text-[42px] font-bold text-[#111111] leading-tight max-w-[900px] mx-auto text-center mb-[15px]"
            >
              Compare Team Members with AI Context.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg"
              style={{ color: "#7B8796" }}
            >
              Understanding performance gaps to coach better, not micromanage.
            </motion.p>
          </div>

          {/* Two Comparison Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Sarah Johnson Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-white rounded-xl p-6 shadow-sm"
            >
              {/* Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden bg-[#F3F4F6]">
                  <img
                    src="/Sarah_img.webp"
                    alt="Sarah Johnson"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="text-[#000000] font-bold text-lg">
                    Sarah Johnson
                  </h3>
                  <p className="text-[#5c5c5c] text-sm">Marketing Manager</p>
                </div>
              </div>

              {/* Metrics */}
              <div className="mb-6 space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[#5c5c5c] text-sm">Focus Time</span>
                    <span className="text-[#000000] font-semibold">
                      4h 30m (75%)
                    </span>
                  </div>
                  <div className="h-2 bg-[#E6E8EC] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#28B4AE] rounded-full"
                      style={{ width: "75%" }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[#5c5c5c] text-sm">
                      Productive Time
                    </span>
                    <span className="text-[#000000] font-semibold">5h 45m</span>
                  </div>
                  <div className="h-2 bg-[#E6E8EC] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#28B4AE] rounded-full"
                      style={{ width: "90%" }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[#5c5c5c] text-sm">
                      Stuck Signals
                    </span>
                    <span className="text-[#000000] font-semibold">
                      2 instances
                    </span>
                  </div>
                  <div className="h-2 bg-[#E6E8EC] rounded-full overflow-hidden flex gap-1">
                    <div
                      className="h-full bg-[#28B4AE] rounded-full"
                      style={{ width: "12%" }}
                    ></div>
                    <div
                      className="h-full bg-[#28B4AE] rounded-full"
                      style={{ width: "12%" }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[#5c5c5c] text-sm">
                      Repetitive Work
                    </span>
                    <span className="text-[#000000] font-semibold">1h 15m</span>
                  </div>
                  <div className="h-2 bg-[#E6E8EC] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#28B4AE] rounded-full"
                      style={{ width: "25%" }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* AI Analysis Box */}
              <div
                className="rounded-lg p-4"
                style={{ backgroundColor: "#E4EBF4" }}
              >
                <div className="mb-2">
                  <span className="text-[#6D8FC0] font-bold text-sm">
                    AI Analysis
                  </span>
                </div>
                <p className="text-[#2F3744] text-sm leading-relaxed">
                  Sarah is highly productive but spent significant time on
                  manual reporting. Recommend automation tools to free up 1+
                  hour daily for strategic work.
                </p>
              </div>
            </motion.div>

            {/* Mike Chen Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="bg-white rounded-xl p-6 shadow-sm"
            >
              {/* Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden bg-[#F3F4F6]">
                  <img
                    src="/Mike_img.webp"
                    alt="Mike Chen"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="text-[#000000] font-bold text-lg">
                    Mike Chen
                  </h3>
                  <p className="text-[#5c5c5c] text-sm">Marketing Manager</p>
                </div>
              </div>

              {/* Metrics */}
              <div className="mb-6 space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[#5c5c5c] text-sm">Focus Time</span>
                    <span className="text-[#F27A53] font-semibold">
                      2h 10m (40%)
                    </span>
                  </div>
                  <div className="h-2 bg-[#E6E8EC] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#F27A53] rounded-full"
                      style={{ width: "40%" }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[#5c5c5c] text-sm">
                      Productive Time
                    </span>
                    <span className="text-[#F27A53] font-semibold">3h 20m</span>
                  </div>
                  <div className="h-2 bg-[#E6E8EC] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#F27A53] rounded-full"
                      style={{ width: "30%" }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[#5c5c5c] text-sm">
                      Stuck Signals
                    </span>
                    <span className="text-[#E55C75] font-semibold">
                      6 instances
                    </span>
                  </div>
                  <div className="h-2 bg-[#E6E8EC] rounded-full overflow-hidden flex gap-1">
                    <div
                      className="h-full bg-[#E55C75] rounded-full"
                      style={{ width: "10%" }}
                    ></div>
                    <div
                      className="h-full bg-[#E55C75] rounded-full"
                      style={{ width: "10%" }}
                    ></div>
                    <div
                      className="h-full bg-[#E55C75] rounded-full"
                      style={{ width: "10%" }}
                    ></div>
                    <div
                      className="h-full bg-[#E55C75] rounded-full"
                      style={{ width: "10%" }}
                    ></div>
                    <div
                      className="h-full bg-[#E55C75] rounded-full"
                      style={{ width: "10%" }}
                    ></div>
                    <div
                      className="h-full bg-[#E55C75] rounded-full"
                      style={{ width: "10%" }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[#5c5c5c] text-sm">
                      Repetitive Work
                    </span>
                    <span className="text-[#E55C75] font-semibold">2h 45m</span>
                  </div>
                  <div className="h-2 bg-[#E6E8EC] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#E55C75] rounded-full"
                      style={{ width: "40%" }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* AI Analysis Box */}
              <div
                className="rounded-lg p-4"
                style={{ backgroundColor: "#F5E7ED" }}
              >
                <div className="mb-2">
                  <span className="text-[#E55C75] font-bold text-sm">
                    AI Analysis
                  </span>
                </div>
                <p className="text-[#2F3744] text-sm leading-relaxed">
                  Mike faced multiple approval delays and spent excessive time
                  on repetitive Excel work. Recommend workflow optimization and
                  manager check-in to unblock projects.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Team Recommendation Box */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="rounded-lg px-6 py-4 w-full border border-dotted"
            style={{
              backgroundColor: "#E6FAFF",
              borderColor: "#5CBFCC",
            }}
          >
            <div className="mb-2">
              <span className="text-[#000000] text-sm font-semibold">
                Team Recommendation
              </span>
            </div>
            <p className="text-[#000000] text-sm leading-relaxed">
              <span className="font-bold">
                This is coaching data, not surveillance data.
              </span>{" "}
              Sarah needs automation support to eliminate manual work, while
              Mike requires workflow restructuring and may need help unblocking
              his projects. Both would benefit from streamlined approval
              processes. TeamGrid helps you support your team, not police them.
            </p>
          </motion.div>
        </div>
      </section>

      {/* AI-Powered Problem Detection Section - disabled per new design */}
      {false && (
        <section className="relative py-[57.5px] px-4 sm:px-6 md:px-8 lg:px-12 bg-black">
          <div className="max-w-[1150px] mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-[15px]"
                style={{
                  background: "rgba(0, 217, 217, 0.06)",
                  border: "1px solid rgba(0, 217, 217, 0.15)",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#28B4AE",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                <Brain className="w-4 h-4" style={{ color: "#28B4AE" }} />
                <span>ORGANIZATION INTELLIGENCE</span>
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-[28px] sm:text-[32px] md:text-[36px] lg:text-[42px] font-bold text-white leading-tight max-w-[900px] mx-auto text-center mb-[15px]"
              >
                AI-Powered Problem Detection
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-white text-lg"
              >
                TeamGrid doesn't just track activity. It detects problems and
                tells you how to fix them.
              </motion.p>
            </div>

            {/* Two Row Layout - Each row has card on left and content on right */}
            <div className="space-y-8">
              {/* Row 1: Issues Detected + AI Spots Problems */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                {/* Left: Issues Detected Card */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  className="bg-white/5 border border-white/10 rounded-xl p-6"
                >
                  <div className="flex items-center gap-2 mb-6">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <h3 className="text-white font-bold text-xl">
                      Issues Detected
                    </h3>
                  </div>

                  {/* First Issue Card */}
                  <div className="bg-white/5 border border-red-500/30 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3 mb-3">
                      <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="text-white font-semibold mb-2">
                          Approval Bottleneck - Marketing Team
                        </h4>
                        <p className="text-gray-400 text-sm mb-1">
                          Impact: 4 team members blocked for 2+ days
                        </p>
                        <p className="text-gray-400 text-sm">
                          Affected: Sarah Chen, Mike Rodriguez, Lisa Park, Tom
                          Wilson
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <span className="px-3 py-1 bg-red-500 text-white text-xs font-semibold rounded-md">
                        High Priority
                      </span>
                      <span className="px-3 py-1 bg-gray-700 text-white text-xs font-semibold rounded-md">
                        12.5 hrs lost
                      </span>
                    </div>
                  </div>

                  {/* Second Issue Card */}
                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <Settings className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="text-white font-semibold mb-2">
                          Repetitive Manual Work Detected
                        </h4>
                        <p className="text-gray-400 text-sm mb-1">
                          Pattern: Excel reporting consuming 8.5 hrs/week
                        </p>
                        <p className="text-gray-400 text-sm">
                          Automation Potential: High - CRM integration available
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <span className="px-3 py-1 bg-green-500 text-white text-xs font-semibold rounded-md">
                        Medium Priority
                      </span>
                    </div>
                  </div>
                </motion.div>

                {/* Right: AI Spots Problems Content */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                  className="flex flex-col"
                >
                  <h3 className="text-white font-bold text-2xl mb-4">
                    AI Spots Problems Before They Become Crises
                  </h3>
                  <p className="text-gray-400 text-base mb-6">
                    Automatic detection of issues that traditional monitoring
                    tools miss entirely.
                  </p>
                  <ul className="space-y-4">
                    {[
                      "Detects stuck work and repeated delays automatically",
                      "Identifies communication gaps causing project slowdowns",
                      "Flags team members at risk of burnout",
                      "Catches manual processes eating productive time",
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-4 h-4 rounded-full bg-[#28B4AE] flex items-center justify-center flex-shrink-0 mt-0.5"></div>
                        <span
                          className="text-base"
                          style={{ color: "#9CA3AF" }}
                        >
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </div>

              {/* Row 2: Performance Improvements + Get Specific Recommendations */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                {/* Left: Performance Improvements Card */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  className="bg-white/5 border border-white/10 rounded-xl p-6"
                >
                  <div className="flex items-center gap-2 mb-6">
                    <Check className="w-5 h-5 text-green-500" />
                    <h3 className="text-white font-bold text-xl">
                      Performance Improvements
                    </h3>
                  </div>

                  {/* First Improvement Card */}
                  <div className="bg-gray-800/50 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3 mb-3">
                      <Send className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="text-white font-semibold mb-2">
                          Streamline Approval Workflow
                        </h4>
                        <p className="text-gray-400 text-sm mb-1">
                          Action: Implement async approval system for marketing
                          assets
                        </p>
                        <p className="text-gray-400 text-sm mb-1">
                          Expected Impact: Save 12.5 hrs/week, unblock 4 team
                          members
                        </p>
                        <p className="text-gray-400 text-sm">
                          Effort: Medium (2-3 days implementation)
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <span className="px-3 py-1 bg-green-500 text-white text-xs font-semibold rounded-md">
                        High Impact
                      </span>
                      <span className="px-3 py-1 bg-green-500 text-white text-xs font-semibold rounded-md">
                        Quick Win
                      </span>
                    </div>
                  </div>

                  {/* Second Improvement Card */}
                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <Zap className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="text-white font-semibold mb-2">
                          Automate Excel Reporting
                        </h4>
                        <p className="text-gray-400 text-sm mb-1">
                          Action: Set up automated CRM → Dashboard pipeline
                        </p>
                        <p className="text-gray-400 text-sm">
                          Expected Impact: Save 8.5 hrs/week team-wide
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <span className="px-3 py-1 bg-green-500 text-white text-xs font-semibold rounded-md">
                        High ROI
                      </span>
                    </div>
                  </div>
                </motion.div>

                {/* Right: Get Specific Recommendations Content */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.2 }}
                  className="flex flex-col"
                >
                  <h3 className="text-white font-bold text-2xl mb-4">
                    Get Specific, Actionable Recommendations
                  </h3>
                  <p className="text-gray-400 text-base mb-6">
                    AI doesn't just tell you what's wrong—it tells you exactly
                    what to do about it.
                  </p>
                  <ul className="space-y-4">
                    {[
                      "AI suggests exactly what to fix and why",
                      "Prioritizes improvements by potential impact",
                      "Provides context-aware solutions for your team",
                      "Tracks implementation progress over time",
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-4 h-4 rounded-full bg-[#28B4AE] flex items-center justify-center flex-shrink-0 mt-0.5"></div>
                        <span
                          className="text-base"
                          style={{ color: "#9CA3AF" }}
                        >
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Why Smart Leaders Choose Intelligence Over Surveillance Section - disabled per new design */}
      {false && (
        <section className="relative py-[57.5px] px-4 sm:px-6 md:px-8 lg:px-12 bg-black">
          <div className="max-w-[1150px] mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-[15px]"
                style={{
                  background: "rgba(0, 217, 217, 0.06)",
                  border: "1px solid rgba(0, 217, 217, 0.15)",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#28B4AE",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                <ExternalLink
                  className="w-4 h-4"
                  style={{ color: "#28B4AE" }}
                />
                <span>COMPARISON</span>
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-[28px] sm:text-[32px] md:text-[36px] lg:text-[42px] font-bold text-white leading-tight max-w-[900px] mx-auto text-center mb-12"
              >
                Why Smart Leaders Choose Intelligence Over Surveillance
              </motion.h2>
            </div>

            {/* Comparison Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white/5 border border-white/10 rounded-xl p-8 mb-8 max-w-[900px] mx-auto"
            >
              <div className="flex flex-col md:flex-row gap-8 relative">
                {/* Left Column: Traditional Employee Monitoring */}
                <div className="flex-1">
                  <h3 className="text-red-500 font-bold text-xl mb-6">
                    Traditional Employee Monitoring
                  </h3>
                  <ul className="space-y-4">
                    {[
                      "Screenshot capture every few minutes",
                      "Mouse/keyboard activity tracking",
                      "GPS location monitoring",
                      "Raw activity data dumps",
                      "Time tracking without context",
                      "You still don't know what's actually wrong",
                      "Breeds distrust and resentment",
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <span className="text-white text-base">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Vertical Separator */}
                <div className="hidden md:block w-px bg-white/10 self-stretch -my-8"></div>

                {/* Right Column: TeamGrid Workforce Intelligence */}
                <div className="flex-1">
                  <h3 className="text-green-500 font-bold text-xl mb-6">
                    TeamGrid Workforce Intelligence
                  </h3>
                  <ul className="space-y-4">
                    {[
                      "AI conversational interface",
                      "Signal over surveillance approach",
                      "Bottleneck and waste detection",
                      "Narrative insights with context",
                      "Pattern recognition and recommendations",
                      "Know exactly what to fix and why",
                      "Builds transparency and trust",
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-white text-base">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* Teal Banner */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-white/5 border border-[#28B4AE]/30 rounded-xl p-8 text-center"
            >
              <p
                className="text-xl font-semibold mb-2"
                style={{ color: "#28B4AE" }}
              >
                Your team already knows they should be productive.
              </p>
              <p className="text-xl font-semibold" style={{ color: "#9CA3AF" }}>
                What they need is systems that don't waste their time.
              </p>
            </motion.div>
          </div>
        </section>
      )}

      {/* Built for Every Leader Who Needs Clarity Section - ample space above per reference */}
      <section className="relative pt-20 sm:pt-24 md:pt-28 lg:pt-32 pb-[57.5px] px-4 sm:px-6 md:px-8 lg:px-12 bg-white">
        <div className="max-w-[1150px] mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center px-4 py-2 rounded-full mb-[15px]"
              style={{
                background: "rgba(0, 217, 217, 0.06)",
                border: "1px solid rgba(0, 217, 217, 0.15)",
                fontSize: "12px",
                fontWeight: 600,
                color: "#28B4AE",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              <span>USE CASES</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-[28px] sm:text-[32px] md:text-[36px] lg:text-[42px] font-bold text-[#000000] leading-tight max-w-[900px] mx-auto text-center"
            >
              Built for Every Leader Who Needs Clarity
            </motion.h2>
          </div>

          {/* Four Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {/* Card 1: For Founders */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="relative rounded-2xl overflow-hidden shadow-sm min-h-[260px] group"
            >
              <img
                src="/Founder_new.webp"
                alt="For Founders"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />
              <div className="relative z-10 h-full flex flex-col justify-end p-5">
                <div>
                  <div className="inline-flex items-center justify-center w-9 h-9 rounded-xl mb-4 overflow-hidden">
                    <img
                      src="/Logo_Founders.webp"
                      alt="Founders logo"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3
                    className="text-white font-bold text-lg mb-1"
                    style={{ color: "#FFFFFF", WebkitTextFillColor: "#FFFFFF" }}
                  >
                    For Founders
                  </h3>
                  <p className="text-sm leading-relaxed text-[#E5E7EB]">
                    Get a pulse on operations without micromanaging.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Card 2: For CEOs */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="relative rounded-2xl overflow-hidden shadow-sm min-h-[260px] group"
            >
              <img
                src="/CEO_new.webp"
                alt="For CEOs"
                className="absolute inset-0 w-full h-full object-cover object-[center_38%]"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />
              <div className="relative z-10 h-full flex flex-col justify-end p-5">
                <div>
                  <div className="inline-flex items-center justify-center w-9 h-9 rounded-xl mb-4 overflow-hidden">
                    <img
                      src="/For_CEOS_logo.webp"
                      alt="CEOs logo"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3
                    className="text-white font-bold text-lg mb-1"
                    style={{ color: "#FFFFFF", WebkitTextFillColor: "#FFFFFF" }}
                  >
                    For CEOs
                  </h3>
                  <p className="text-sm leading-relaxed text-[#E5E7EB]">
                    Data-driven decisions for resource allocation.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Card 3: For CTOs */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="relative rounded-2xl overflow-hidden shadow-sm min-h-[260px] group"
            >
              <img
                src="/CTO_new.webp"
                alt="For CTOs"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />
              <div className="relative z-10 h-full flex flex-col justify-end p-5">
                <div>
                  <div className="inline-flex items-center justify-center w-9 h-9 rounded-xl mb-4 overflow-hidden">
                    <img
                      src="/for_CTOs_logo.webp"
                      alt="CTOs logo"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3
                    className="text-white font-bold text-lg mb-1"
                    style={{ color: "#FFFFFF", WebkitTextFillColor: "#FFFFFF" }}
                  >
                    For CTOs
                  </h3>
                  <p className="text-sm leading-relaxed text-[#E5E7EB]">
                    Identify technical blockers and optimize flow.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Card 4: For Operations */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="relative rounded-2xl overflow-hidden shadow-sm min-h-[260px] group"
            >
              <img
                src="/operations_new.webp"
                alt="For Operations"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />
              <div className="relative z-10 h-full flex flex-col justify-end p-5">
                <div>
                  <div className="inline-flex items-center justify-center w-9 h-9 rounded-xl mb-4 overflow-hidden">
                    <img
                      src="/For_Operations_logo.webp"
                      alt="Operations logo"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3
                    className="text-white font-bold text-lg mb-1"
                    style={{ color: "#FFFFFF", WebkitTextFillColor: "#FFFFFF" }}
                  >
                    For Operations
                  </h3>
                  <p className="text-sm leading-relaxed text-[#E5E7EB]">
                    Eliminate waste and optimize workflows.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* From Setup to Insights Section (black) */}
      <section className="how-it-works-section relative py-[57.5px] px-4 sm:px-6 md:px-8 lg:px-12 bg-black">
        <div className="max-w-[1150px] mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-[15px]"
              style={{
                background: "rgba(0, 217, 217, 0.06)",
                border: "1px solid rgba(0, 217, 217, 0.15)",
                fontSize: "12px",
                fontWeight: 600,
                color: "#28B4AE",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              <span>HOW IT WORKS</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-[28px] sm:text-[32px] md:text-[36px] lg:text-[42px] font-bold text-white/80 leading-tight max-w-[900px] mx-auto text-center mb-3"
            >
              From Setup to Insights in Minutes
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-white/80 text-base max-w-[640px] mx-auto"
            >
              No complex implementation. No IT department required.
              <br />
              Get started in under 5 minutes.
            </motion.p>
          </div>

          {/* Steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {/* Quick Install */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="rounded-xl border border-white/25 bg-[#141414] px-6 py-8 flex flex-col items-center text-center"
            >
              <div className="w-[58px] h-[58px] rounded-2xl flex items-center justify-center mb-4 overflow-hidden">
                <img
                  src="/how_it_works_install_icon.webp"
                  alt="Quick Install"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-white/80 font-semibold text-lg mb-2">
                Quick Install
              </h3>
              <p className="text-sm text-white/80 leading-relaxed">
                Lightweight desktop agent installs in seconds. Works quietly in
                the background so your team will not notice any performance
                impact.
              </p>
            </motion.div>

            {/* Auto Analysis */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="rounded-xl border border-white/25 bg-[#141414] px-6 py-8 flex flex-col items-center text-center"
            >
              <div className="w-[58px] h-[58px] rounded-2xl flex items-center justify-center mb-4 overflow-hidden">
                <img
                  src="/how_it_works_auto_icon.webp"
                  alt="Auto Analysis"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-white/80 font-semibold text-lg mb-2">
                Auto Analysis
              </h3>
              <p className="text-sm text-white/80 leading-relaxed">
                TeamGrid automatically captures work patterns, categorizes
                activities, and builds a picture of how your team operates with
                no manual input needed.
              </p>
            </motion.div>

            {/* Ask & Act */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="rounded-xl border border-white/25 bg-[#141414] px-6 py-8 flex flex-col items-center text-center"
            >
              <div className="w-[58px] h-[58px] rounded-2xl flex items-center justify-center mb-4 overflow-hidden">
                <img
                  src="/how_it_works_chat_icon.webp"
                  alt="Ask & Act"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-white/80 font-semibold text-lg mb-2">
                Ask &amp; Act
              </h3>
              <p className="text-sm text-white/80 leading-relaxed">
                Start asking questions immediately. Get answers that help you
                make better decisions, support your team, and drive real
                improvements.
              </p>
            </motion.div>
          </div>

          {/* CTA */}
          <div className="text-center">
            {/* <button className="inline-flex items-center justify-center px-8 py-3 rounded-full text-sm font-semibold text-black bg-[#28B4AE] hover:bg-[#22a598] transition-colors">
              Get Started Free
            </button> */}
          </div>
        </div>
      </section>

      {/* Intelligence That Delivers Section - COMMENTED OUT */}
      {false && (
        <section className="relative py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8 lg:px-8 bg-black">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 sm:mb-16 space-y-4 sm:space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="flex justify-center mb-6 sm:mb-8"
              >
                <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-black/40 backdrop-blur-xl border border-[#28B4AE]/30">
                  <Brain className="w-3 h-3 sm:w-4 sm:h-4 text-[#28B4AE]" />
                  <span className="text-xs sm:text-sm font-medium text-gray-300 tracking-wide">
                    INSIGHTS THAT MATTER
                  </span>
                </div>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-[28px] sm:text-[32px] md:text-[36px] lg:text-[42px] xl:text-[48px] font-bold text-white max-w-[900px] mx-auto text-center px-4"
              >
                Intelligence That Delivers
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-400 max-w-4xl mx-auto leading-relaxed px-4"
              >
                Do you ever feel your team talks about AI, but never truly
                adopts it? TeamGrid uncovers the real patterns behind that —
                helping you make your team leaner, smarter, and genuinely
                AI-driven.
              </motion.p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-4">
                {features.map((feature, index) => {
                  const IconComponent = feature.icon;
                  const isSelected = selectedFeature === index;
                  return (
                    <motion.button
                      key={feature.id}
                      onClick={() => setSelectedFeature(index)}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                      whileHover={
                        !isSelected
                          ? {
                              scale: 1.02,
                              x: 4,
                              transition: { duration: 0.2, ease: "easeOut" },
                            }
                          : {}
                      }
                      whileTap={{ scale: 0.98 }}
                      className={`w-full text-left p-6 rounded-2xl transition-all duration-300 relative overflow-hidden ${
                        isSelected
                          ? "bg-white/10"
                          : "bg-white/5 border border-white/10 hover:bg-white/8"
                      }`}
                      style={
                        isSelected
                          ? {
                              border: "2px solid transparent",
                              backgroundImage:
                                "linear-gradient(black, black), linear-gradient(to bottom right, #3b82f6, #8b5cf6)",
                              backgroundOrigin: "border-box",
                              backgroundClip: "padding-box, border-box",
                              boxShadow: "0 0 20px rgba(59, 130, 246, 0.3)",
                            }
                          : {}
                      }
                    >
                      {!isSelected && (
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/5 to-purple-500/0 opacity-0"
                          whileHover={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        />
                      )}

                      {!isSelected && (
                        <motion.div
                          className="absolute inset-0 rounded-2xl border border-transparent"
                          style={{
                            background:
                              "linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(139, 92, 246, 0.2))",
                            opacity: 0,
                            boxShadow: "0 0 0 1px rgba(59, 130, 246, 0.1)",
                          }}
                          whileHover={{
                            opacity: 1,
                            boxShadow:
                              "0 0 0 1px rgba(59, 130, 246, 0.3), 0 4px 12px rgba(59, 130, 246, 0.15)",
                          }}
                          transition={{ duration: 0.3 }}
                        />
                      )}
                      <div className="flex items-center gap-4 relative z-10">
                        <motion.div
                          className={`p-3 rounded-lg ${
                            isSelected
                              ? "bg-gradient-to-br from-blue-500/30 to-purple-500/30"
                              : "bg-white/5"
                          }`}
                          whileHover={
                            !isSelected
                              ? {
                                  scale: 1.1,
                                  rotate: 5,
                                  transition: { duration: 0.2 },
                                }
                              : {}
                          }
                        >
                          <IconComponent
                            className={`w-6 h-6 ${feature.iconColor}`}
                          />
                        </motion.div>
                        <motion.span
                          className={`text-lg font-semibold ${
                            isSelected ? "text-white" : "text-white/80"
                          }`}
                          whileHover={
                            !isSelected
                              ? {
                                  x: 2,
                                  transition: { duration: 0.2 },
                                }
                              : {}
                          }
                        >
                          {feature.title}
                        </motion.span>
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              <motion.div
                key={selectedFeature}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8"
              >
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/20 border border-blue-500/30 mb-6">
                  <Info className="w-4 h-4 text-blue-300" />
                  <span className="text-xs font-medium text-gray-200 uppercase tracking-wide">
                    AI-POWERED
                  </span>
                </div>

                <h3 className="text-[24px] sm:text-[28px] md:text-[32px] font-bold text-white mb-6">
                  {features[selectedFeature].title}
                </h3>

                <p className="text-white/90 text-lg mb-4 leading-relaxed">
                  {features[selectedFeature].description}
                </p>
                <p className="text-white/80 text-base mb-8 leading-relaxed">
                  {features[selectedFeature].details}
                </p>

                <div className="bg-black/50 border border-white/10 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="text-6xl text-blue-300 font-serif leading-none">
                      "
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-white/60 uppercase tracking-wide mb-2">
                        Real Example
                      </p>
                      <p className="text-white/90 text-base leading-relaxed">
                        {features[selectedFeature].example}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Enterprise-Grade Security, Privacy-First Design Section */}
      <section className="relative py-[57.5px] px-4 sm:px-6 md:px-8 lg:px-12 bg-white">
        <div className="max-w-[1150px] mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center px-4 py-2 rounded-full mb-[15px]"
              style={{
                background: "rgba(0, 217, 217, 0.06)",
                border: "1px solid rgba(0, 217, 217, 0.15)",
                fontSize: "12px",
                fontWeight: 600,
                color: "#28B4AE",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              <span>SECURITY & PRIVACY</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-[28px] sm:text-[32px] md:text-[36px] lg:text-[42px] font-bold text-black leading-tight max-w-[900px] mx-auto text-center mb-3"
            >
              Enterprise-Grade Security, Privacy-First Design
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-base text-gray-500 max-w-[640px] mx-auto text-center"
            >
              Advanced Security. Transparent Privacy.
            </motion.p>
          </div>

          {/* Four Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {/* Card 1: SOC 2 Type II - light pink/lavender */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="relative rounded-xl p-6 text-left overflow-hidden bg-[#F2EEF8]"
            >
              <div className="absolute -right-10 top-1/2 -translate-y-1/2 w-44 h-44 opacity-[0.62] pointer-events-none z-[1]">
                <img
                  src="/Vector_1_back.webp"
                  alt=""
                  className="w-full h-full object-contain"
                  style={{ filter: "brightness(2) saturate(1.08)" }}
                  aria-hidden
                />
              </div>
              <div className="relative z-[2]">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <img
                    src="/Vector_1icon.webp"
                    alt="SOC 2 Type II"
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <h3 className="text-black font-bold text-xl mb-3">
                  SOC 2 Type II
                </h3>
                <p className="text-sm text-black/80 leading-relaxed">
                  Audited security controls and compliance standards
                </p>
              </div>
            </motion.div>

            {/* Card 2: GDPR & CCPA Ready - light pink/lavender */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="relative rounded-xl p-6 text-left overflow-hidden bg-[#F5F2F4]"
            >
              <div className="absolute -right-10 top-1/2 -translate-y-1/2 w-44 h-44 opacity-[0.62] pointer-events-none z-[1]">
                <img
                  src="/vector_2_back.webp"
                  alt=""
                  className="w-full h-full object-contain"
                  style={{ filter: "brightness(2) saturate(1.08)" }}
                  aria-hidden
                />
              </div>
              <div className="relative z-[2]">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <img
                    src="/Vector_2icon.webp"
                    alt="GDPR & CCPA Ready"
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <h3 className="text-black font-bold text-xl mb-3">
                  GDPR & CCPA Ready
                </h3>
                <p className="text-sm text-black/80 leading-relaxed">
                  Full privacy compliance with data regulations
                </p>
              </div>
            </motion.div>

            {/* Card 3: No Screenshots - light mint/teal */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="relative rounded-xl p-6 text-left overflow-hidden bg-[#CCECEB]"
            >
              <div
                className="absolute right-0 top-0 w-44 h-44 opacity-90 pointer-events-none z-[1]"
                style={{ transform: "translate(20%, -15%)" }}
              >
                <img
                  src="/Vector_3_back.webp"
                  alt=""
                  className="w-full h-full object-contain"
                  style={{ filter: "brightness(1.5) contrast(1.15)" }}
                  aria-hidden
                />
              </div>
              <div className="relative z-[2]">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <img
                    src="/Vector_3icon.webp"
                    alt="No Screenshots"
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <h3 className="text-black font-bold text-xl mb-3">
                  No Screenshots
                </h3>
                <p className="text-sm text-black/80 leading-relaxed">
                  Respects employee privacy with signal-based tracking
                </p>
              </div>
            </motion.div>

            {/* Card 4: Encrypted - light blue */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="relative rounded-xl p-6 text-left overflow-hidden bg-[#D9E1EF]"
            >
              <div className="absolute -right-10 top-1/2 -translate-y-1/2 w-44 h-44 opacity-[0.62] pointer-events-none z-[1]">
                <img
                  src="/Vector_5_back.webp"
                  alt=""
                  className="w-full h-full object-contain"
                  style={{ filter: "brightness(2) saturate(1.08)" }}
                  aria-hidden
                />
              </div>
              <div className="relative z-[2]">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <img
                    src="/Vector_5logo.webp"
                    alt="Encrypted"
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <h3 className="text-black font-bold text-xl mb-3">Encrypted</h3>
                <p className="text-sm text-black/80 leading-relaxed">
                  End-to-end encryption for all your data
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section — matches AlternateLanding layout */}
      <section
        id="pricing"
        className="relative flex flex-col items-center w-full flex-none self-stretch z-[8] overflow-visible pb-12 pt-16 px-4 md:pt-[100px] md:pb-[80px] md:px-[80px] gap-10 md:gap-16"
        style={{
          background: "rgba(0, 0, 0, 0.02)",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <div
          className="flex flex-col items-center text-center"
          style={{ gap: 20, maxWidth: 680 }}
        >
          <h2
            className="font-extrabold text-black text-center"
            style={{
              fontSize: 48,
              lineHeight: "57px",
              letterSpacing: "-1.56px",
            }}
          >
            Simple, Honest Pricing
          </h2>
          <p
            className="text-center"
            style={{
              fontSize: 20,
              lineHeight: "31px",
              color: "rgba(0, 0, 0, 0.6)",
              fontWeight: 400,
            }}
          >
            {/* Start free. */}
            Upgrade when you're ready. No hidden fees, no surprises.
          </p>
        </div>
        <div className="relative w-full max-w-[1200px] mx-auto z-10 flex flex-col items-center">
          {/* Mobile Arrows */}
          <div className="flex md:hidden w-full items-center justify-between absolute top-[55%] -translate-y-1/2 px-2 sm:px-4 z-20 pointer-events-none">
            <button 
              onClick={() => setCurrentPricingSlide(s => Math.max(0, s - 1))}
              disabled={currentPricingSlide === 0}
              className="pointer-events-auto w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border border-gray-200 disabled:opacity-0 transition-opacity"
              aria-label="Previous pricing"
            >
              <ChevronDown className="w-6 h-6 rotate-90 text-black" />
            </button>
            <button 
              onClick={() => setCurrentPricingSlide(s => Math.min(3, s + 1))}
              disabled={currentPricingSlide === 3}
              className="pointer-events-auto w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border border-gray-200 disabled:opacity-0 transition-opacity"
              aria-label="Next pricing"
            >
              <ChevronDown className="w-6 h-6 -rotate-90 text-black" />
            </button>
          </div>

          <div
            className="w-[282px] md:w-full overflow-hidden md:overflow-visible"
            style={{ paddingTop: 48 }}
          >
            <div 
              className="flex flex-nowrap md:justify-center items-stretch gap-0 md:gap-6 transition-transform duration-300 ease-in-out md:!transform-none"
              style={{
                transform: `translateX(-${currentPricingSlide * 282}px)`
              }}
            >
          {/* Starter */}
          {/* <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative flex flex-col flex-shrink-0 bg-white box-border"
            style={{
              width: 282,
              minHeight: 546.75,
              padding: "36px 37px 36px 37px",
              border: "1px solid rgba(0, 0, 0, 0.2)",
              borderRadius: 24,
            }}
          >
            <h3
              className="font-bold text-black"
              style={{ fontSize: 22, lineHeight: "35px" }}
            >
              Starter
            </h3>
            <p
              className="text-black"
              style={{
                fontSize: 14,
                lineHeight: "22px",
                marginTop: 8,
                marginBottom: 10,
              }}
            >
              For small teams getting started
            </p>
            <div style={{ marginBottom: 18 }}>
              <span
                className="font-extrabold text-black"
                style={{
                  fontSize: 48,
                  lineHeight: "77px",
                  letterSpacing: "-1.44px",
                }}
              >
                {formatPrice('free')}
              </span>
              <span
                className="ml-2 inline-block"
                style={{
                  fontSize: 13,
                  lineHeight: "20px",
                  color: "#fff",
                  fontWeight: 600,
                  background: "linear-gradient(135deg, #00C8B3, #00A89C)",
                  padding: "3px 10px",
                  borderRadius: 20,
                  verticalAlign: "middle",
                }}
              >
                5-day free trial
              </span>
            </div>
            <ul
              className="flex flex-col"
              style={{ gap: "21px", marginBottom: 24 }}
            >
              {[
                "Up to 5 team members",
                "Monthly and Daily Reports",
                "Team Management and Project Tracking",
              ].map((f, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-black"
                  style={{ fontSize: 14, lineHeight: "22px" }}
                >
                  <Check
                    className="w-[18px] h-[18px] flex-shrink-0 mt-0.5"
                    style={{ color: "#34A851" }}
                    strokeWidth={2.5}
                  />
                  {f}
                </li>
              ))}
            </ul>
            <motion.a
              href="/register"
              className="mt-auto flex items-center justify-center w-full text-center text-black font-semibold box-border"
              style={{
                height: 51,
                borderRadius: 12,
                fontSize: 14,
                lineHeight: "17px",
                border: "1px solid rgba(0, 0, 0, 0.1)",
                background: "transparent",
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Start Trial
            </motion.a>
          </motion.div> */}

          {/* Team */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative flex flex-col flex-shrink-0 bg-white box-border"
            style={{
              width: 282,
              minHeight: 546.75,
              padding: "36px 37px 36px 37px",
              border: "1px solid rgba(0, 0, 0, 0.2)",
              borderRadius: 24,
            }}
          >
            <h3
              className="font-bold text-black"
              style={{ fontSize: 22, lineHeight: "35px" }}
            >
              Team
            </h3>
            <p
              className="text-black"
              style={{
                fontSize: 14,
                lineHeight: "22px",
                marginTop: 8,
                marginBottom: 10,
              }}
            >
              For growing teams
            </p>
            <div style={{ marginBottom: 18 }}>
              <span
                className="font-extrabold text-[#1F2937]"
                style={{
                  fontSize: 40,
                  lineHeight: "64px",
                  letterSpacing: "-1.44px",
                }}
              >
                {formatPrice('standard')}
              </span>
              <span
                className="ml-1"
                style={{
                  fontSize: 15,
                  lineHeight: "24px",
                  color: "#000",
                  fontWeight: 400,
                }}
              >
                / user / month
              </span>
            </div>
            <ul
              className="flex flex-col"
              style={{ gap: "21px", marginBottom: 24 }}
            >
              {[
                "Unlimited team members",
                "Monthly and Daily Reports",
                "Team Management and Project Tracking",
              ].map((f, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-black"
                  style={{ fontSize: 14, lineHeight: "22px" }}
                >
                  <Check
                    className="w-[18px] h-[18px] flex-shrink-0 mt-0.5"
                    style={{ color: "#34A851" }}
                    strokeWidth={2.5}
                  />
                  {f}
                </li>
              ))}
            </ul>
            <motion.a
              href="/register"
              className="mt-auto flex items-center justify-center w-full text-center text-black font-semibold box-border"
              style={{
                height: 51,
                borderRadius: 12,
                fontSize: 14,
                lineHeight: "17px",
                border: "1px solid rgba(0, 0, 0, 0.1)",
                background: "transparent",
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Get Started
            </motion.a>
          </motion.div>

          {/* Advanced - Most Popular */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="relative flex flex-col flex-shrink-0 bg-white box-border"
            style={{
              width: 282,
              minHeight: 546.75,
              padding: "36px 37px 36px 37px",
              border: "1px solid #00D1C1",
              borderRadius: 24,
              boxShadow: "0px 0px 80px -20px rgba(0, 217, 217, 0.3)",
              zIndex: 10,
            }}
          >
            <div
              className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center rounded-lg text-black uppercase font-bold"
              style={{
                top: -16,
                background: "#00D1C1",
                borderRadius: 8,
                padding: "7px 18px",
                fontSize: 11,
                lineHeight: "18px",
                letterSpacing: "0.44px",
                zIndex: 9999,
              }}
            >
              MOST POPULAR
            </div>
            <h3
              className="font-bold text-black"
              style={{ fontSize: 22, lineHeight: "35px" }}
            >
              Advanced
            </h3>
            <p
              className="text-black"
              style={{
                fontSize: 14,
                lineHeight: "22px",
                marginTop: 8,
                marginBottom: 10,
              }}
            >
              For teams that want AI power
            </p>
            <div style={{ marginBottom: 18 }}>
              <span
                className="font-extrabold text-[#1F2937]"
                style={{
                  fontSize: 40,
                  lineHeight: "64px",
                  letterSpacing: "-1.44px",
                }}
              >
                {formatPrice('premium')}
              </span>
              <span
                className="ml-1"
                style={{
                  fontSize: 15,
                  lineHeight: "24px",
                  color: "#000",
                  fontWeight: 400,
                }}
              >
                / user / month
              </span>
            </div>
            <ul
              className="flex flex-col"
              style={{ gap: "21px", marginBottom: 24 }}
            >
              {[
                "Everything in Basic",
                "Ask TeamGrid",
                "Organization Intelligence",
              ].map((f, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-black"
                  style={{ fontSize: 14, lineHeight: "22px" }}
                >
                  <Check
                    className="w-[18px] h-[18px] flex-shrink-0 mt-0.5"
                    style={{ color: "#34A851" }}
                    strokeWidth={2.5}
                  />
                  {f}
                </li>
              ))}
            </ul>
            <motion.a
              href="/register"
              className="mt-auto flex items-center justify-center w-full text-center text-black font-semibold box-border"
              style={{
                height: 49,
                borderRadius: 12,
                fontSize: 14,
                lineHeight: "17px",
                background: "#00D1C1",
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Get Started
            </motion.a>
          </motion.div>

          {/* Enterprise */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="relative flex flex-col flex-shrink-0 bg-white box-border"
            style={{
              width: 282,
              minHeight: 546.75,
              padding: "36px 37px 36px 37px",
              border: "1px solid rgba(0, 0, 0, 0.2)",
              borderRadius: 24,
            }}
          >
            <h3
              className="font-bold text-black"
              style={{ fontSize: 22, lineHeight: "35px" }}
            >
              Enterprise
            </h3>
            <p
              className="text-black"
              style={{
                fontSize: 14,
                lineHeight: "22px",
                marginTop: 8,
                marginBottom: 10,
              }}
            >
              For large organizations
            </p>
            <div style={{ marginBottom: 18 }}>
              <span
                className="font-extrabold text-black"
                style={{
                  fontSize: 48,
                  lineHeight: "77px",
                  letterSpacing: "-1.44px",
                }}
              >
                Custom
              </span>
            </div>
            <ul
              className="flex flex-col"
              style={{ gap: "21px", marginBottom: 24 }}
            >
              {["All Features", "Custom integrations", "Custom Reports"].map(
                (f, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-black"
                    style={{ fontSize: 14, lineHeight: "22px" }}
                  >
                    <Check
                      className="w-[18px] h-[18px] flex-shrink-0 mt-0.5"
                      style={{ color: "#34A851" }}
                      strokeWidth={2.5}
                    />
                    {f}
                  </li>
                ),
              )}
            </ul>
            <motion.a
              href="/contact-us"
              className="mt-auto flex items-center justify-center w-full text-center text-black font-semibold box-border"
              style={{
                height: 51,
                borderRadius: 12,
                fontSize: 14,
                lineHeight: "17px",
                border: "1px solid rgba(0, 0, 0, 0.1)",
                background: "transparent",
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Contact Sales
            </motion.a>
          </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Success Section */}
      <Testimonials autoScroll={true} scrollSpeed={50} />

      {/* FAQ Section */}
      <section className="relative pt-[57.5px] pb-0 px-4 sm:px-6 md:px-8 lg:px-12 bg-[#F3F4F6]">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-[15px]"
              style={{
                background: "rgba(0, 217, 217, 0.06)",
                border: "1px solid rgba(0, 217, 217, 0.15)",
                fontSize: "12px",
                fontWeight: 600,
                color: "#28B4AE",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              <span>Got Questions?</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-[28px] sm:text-[32px] md:text-[36px] lg:text-[42px] font-bold text-white max-w-[900px] mx-auto text-center mb-[15px]"
            >
              Frequently Asked Questions
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg text-white"
              style={{ color: "#555555" }}
            >
              Everything you need to know about how TeamGrid works and the value
              it delivers.
            </motion.p>
          </div>

          {/* FAQ Items */}
          <div className="space-y-4 mb-16">
            {[
              {
                question: "What is TeamGrid?",
                answer:
                  "TeamGrid is an AI-powered workforce intelligence platform that analyzes real work patterns, collaboration signals, and productivity trends to generate summaries, insights, and automation recommendations that help teams improve performance without surveillance.",
              },
              {
                question: "Does TeamGrid invade privacy?",
                answer:
                  "No. TeamGrid is designed as a privacy-first platform. It focuses on aggregated work patterns and operational signals rather than monitoring individual behavior or capturing personal data.",
              },
              {
                question: "How does the tracking work?",
                answer:
                  "TeamGrid analyzes collaboration and workflow signals from connected tools to identify productivity trends, operational bottlenecks, and team performance insights. It does not record screens or monitor personal activity.",
              },
              {
                question: "Can I export reports?",
                answer:
                  "Yes. TeamGrid allows you to export productivity insights and reports so you can share them with leadership teams or integrate them into operational reviews.",
              },
              {
                question: "Can I cancel or change my subscription anytime?",
                answer:
                  "Yes. You can upgrade, downgrade, or cancel your subscription anytime from your account settings without long-term commitments.",
              },
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                onMouseEnter={() => setHoveredFaqIndex(index)}
                onMouseLeave={() => setHoveredFaqIndex(-1)}
                className="rounded-xl p-6 cursor-pointer bg-white"
                style={{
                  border:
                    openFaqIndex === index || hoveredFaqIndex === index
                      ? "1px solid #28B4AE"
                      : "1px solid #D1D5DB",
                  transition: "border-color 0.15s ease-in-out",
                }}
                whileHover={{
                  scale: 1.02,
                  transition: { duration: 0.2 },
                }}
              >
                <div
                  onClick={() =>
                    setOpenFaqIndex(openFaqIndex === index ? -1 : index)
                  }
                  className="w-full flex items-center justify-between"
                >
                  <h3
                    className="text-left font-bold text-[#1A1A1A] text-base"
                    style={{ fontSize: 16, lineHeight: "26px" }}
                  >
                    {faq.question}
                  </h3>
                  {openFaqIndex === index ? (
                    <Minus
                      className="w-5 h-5 flex-shrink-0"
                      style={{ color: "#111111" }}
                    />
                  ) : (
                    <Plus className="w-5 h-5 flex-shrink-0 text-[#111111]" />
                  )}
                </div>
                <AnimatePresence>
                  {openFaqIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <p
                        className="mt-4 text-base"
                        style={{
                          fontSize: 16,
                          lineHeight: "26px",
                          color: "#374151",
                        }}
                      >
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          {/* CTA Section - black bg like Testimonials */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            className="ready-cta-section mt-10 md:mt-20 mb-[-1px] relative left-1/2 -ml-[50vw] -mr-[50vw] w-screen max-w-none px-4 sm:px-6 md:px-8 lg:px-12 py-12 md:py-16 bg-black text-center"
          >
            <h3 className="ready-cta-heading text-[24px] sm:text-[28px] md:text-[32px] lg:text-[36px] font-bold !text-white mb-4">
              Ready to Understand Your Team?
            </h3>
            <p className="text-lg mb-8 text-white/80 max-w-[640px] mx-auto">
              Join hundreds of teams using TeamGrid to make smarter decisions
              and build better workplaces.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
              <motion.a
                href="/register"
                className="bg-[#28B4AE] text-black px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#22a598] transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {/* Start Free Trial */}
                Register
              </motion.a>
              <motion.a
                href="/contact-us"
                className="border border-white text-white/80 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/10 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Talk to Sales
              </motion.a>
            </div>
            <p className="text-sm text-white/80 flex items-center justify-center gap-2">
              <span>
                No credit card required • Full feature access • 5-minute setup
              </span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Section */}
      {blogs.length > 0 && 5 == 4 && (
        <section className="relative py-[57.5px] px-4 sm:px-6 md:px-8 lg:px-8 bg-black">
          <div className="max-w-[1150px] mx-auto">
            {/* Header */}
            <div className="text-center mb-12 sm:mb-16 space-y-4 sm:space-y-6">
              {/* BLOG Tag */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="flex justify-center mb-4 sm:mb-6"
              >
                <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[#28B4AE]/10 border border-[#28B4AE]/30">
                  <FileText className="w-3 h-3 sm:w-4 sm:h-4 text-[#28B4AE]" />
                  <span className="text-xs sm:text-sm font-medium text-gray-300 tracking-wide">
                    LATEST INSIGHTS
                  </span>
                </div>
              </motion.div>

              {/* Main Title */}
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-[32px] sm:text-[36px] md:text-[42px] lg:text-[48px] font-bold text-white mb-4 px-4"
              >
                Latest from Our Blog
              </motion.h2>

              {/* Subtitle */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-base sm:text-lg md:text-xl text-gray-400 px-4"
              >
                Expert insights on AI-powered workforce analytics and team
                productivity
              </motion.p>
            </div>

            {/* Blog Cards */}
            {blogsLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="bg-white/5 border border-white/10 rounded-2xl p-6 animate-pulse"
                  >
                    <div className="h-4 bg-white/10 rounded w-3/4 mb-4"></div>
                    <div className="h-4 bg-white/10 rounded w-full mb-2"></div>
                    <div className="h-4 bg-white/10 rounded w-5/6"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                {blogs.map((blog, index) => (
                  <Link
                    key={blog._id}
                    to={`/blog/${blog.slug}`}
                    className="block"
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                      className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden hover:bg-white/8 hover:border-[#28B4AE]/30 transition-all duration-300 group cursor-pointer h-full flex flex-col"
                    >
                      {/* Featured Image */}
                      {blog.featuredImage &&
                        blog.featuredImage.trim() !== "" && (
                          <div className="h-48 overflow-hidden">
                            <img
                              src={blog.featuredImage}
                              alt={blog.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                console.log(
                                  "Landing page blog image failed to load:",
                                  blog.featuredImage,
                                );
                                e.target.parentElement.style.display = "none";
                              }}
                            />
                          </div>
                        )}

                      <div className="p-6 flex-1 flex flex-col">
                        {/* Category Badge */}
                        <div className="mb-4">
                          <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-medium">
                            {blog.category}
                          </span>
                        </div>

                        {/* Title */}
                        <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#28B4AE] transition-colors line-clamp-2">
                          {blog.title}
                        </h3>

                        {/* Description */}
                        <p className="text-gray-400 text-sm mb-4 leading-relaxed line-clamp-3 flex-1">
                          {blog.description}
                        </p>

                        {/* Meta Info */}
                        <div className="flex items-center justify-between text-xs text-gray-500 mt-auto">
                          <span>{blog.author}</span>

                          {/* Read More Indicator */}
                          <div className="inline-flex items-center gap-2 text-[#28B4AE] group-hover:text-[#28B4AE] transition-colors text-sm font-medium">
                            Read More
                            <ArrowUp className="w-4 h-4 rotate-45 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </Link>
                ))}
              </div>
            )}

            {/* View All Blogs Button */}
            {blogs.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="flex justify-center mt-12"
              >
                <Link
                  to="/blog"
                  className="bg-white/10 border border-white/20 text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/15 transition-all duration-300"
                >
                  View All Blogs
                </Link>
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* Organizational Intelligence Software Section */}
      {/* <section className="relative py-20 px-4 sm:px-6 md:px-8 lg:px-12 bg-black">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            
            <h2 className="text-[28px] sm:text-[32px] md:text-[36px] lg:text-[42px] font-bold text-white leading-tight max-w-[900px] mx-auto text-center">
              Organizational intelligence software.
            </h2>

            
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
              Move beyond basic employee time tracking. TeamGrid uses AI powered automatic tracking to reveal patterns and turn time data into actionable productivity insights.
            </p>

            
            <div className="space-y-6 pt-4">
              {[
                {
                  title: "Automatic, AI-driven tracking",
                  description: "Reduce manual work with automatic time tracking software that accurately captures work patterns across teams."
                },
                {
                  title: "Smart performance and productivity insights",
                  description: "A time tracking and productivity monitoring tool that surfaces inefficiencies and workload gaps fast."
                },
                {
                  title: "Built-in attendance intelligence",
                  description: "AI-led automated attendance tracking and employee attendance monitoring for real-time visibility."
                },
                {
                  title: "Designed for scale and flexibility",
                  description: "An AI-powered time tracking software built to support any workflow from small teams to enterprises."
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-6 h-6 rounded-md bg-[#28B4AE]/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <Check className="w-4 h-4 text-[#28B4AE]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-white leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="flex justify-center pt-8"
            >
              <motion.a
                href="/book-demo"
                className="bg-gradient-to-r from-[#28B4AE] to-[#28B4AE] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:from-[#28B4AE] hover:to-[#00B8A8] transition-all duration-300 shadow-lg shadow-teal-500/20"
                variants={buttonHoverVariants}
                whileHover="hover"
                whileTap="tap"
              >
                See it in action
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </section> */}

      {/* Understand Performance at a Glance Section */}
      {/* <section className="relative py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8 lg:px-8 bg-black">
        <div className="max-w-7xl mx-auto">
          
          <div className="text-center mb-16 space-y-6">
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex justify-center mb-6"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#28B4AE]/20 border border-[#28B4AE]/30">
                <Leaf className="w-4 h-4 text-[#28B4AE]" />
                <span className="text-sm font-medium text-[#28B4AE] tracking-wide">
                  ANALYTICS
                </span>
              </div>
            </motion.div>

            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-[28px] sm:text-[32px] md:text-[36px] lg:text-[42px] font-bold text-white max-w-[900px] mx-auto text-center mb-4"
            >
              Understand Performance at a Glance
            </motion.h2>

            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto"
            >
              TeamGrid categorizes productivity into actionable tiers so you
              know exactly where to focus.
            </motion.p>
          </div>

          
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="flex justify-center items-center gap-3"
          >
            <motion.a
              href="/book-demo"
              className="bg-gradient-to-r from-[#28B4AE] to-[#28B4AE] text-black px-10 py-4 rounded-lg font-semibold text-lg hover:from-[#28B4AE] hover:to-[#00B8A8] transition-all duration-300 relative flex items-center gap-2"
              variants={buttonHoverVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <span>See Your Team</span>
            </motion.a>
          </motion.div>
        </div>
      </section> */}

      {/* A Platform Made for All Section */}
      {/* <section className="relative py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8 lg:px-8 bg-black">
        <div className="max-w-7xl mx-auto">
          
          <div className="text-center mb-16 space-y-6">
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex justify-center mb-6"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 border border-green-500/30">
                <Users className="w-4 h-4 text-green-400" />
                <span className="text-sm font-semibold text-green-400 tracking-wide">
                  USE CASES
                </span>
              </div>
            </motion.div>

            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-[28px] sm:text-[32px] md:text-[36px] lg:text-[42px] font-bold text-white max-w-[900px] mx-auto text-center mb-4"
            >
              A Platform Made for All
            </motion.h2>

            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto"
            >
              Built for teams that want clarity without micromanagement.
            </motion.p>
          </div>

          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:bg-white/8 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-lg bg-cyan-500/20 flex items-center justify-center mb-4">
                <Code className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Every Industry
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Works across tech, agencies, education, HR and remote teams.
              </p>
            </motion.div>

            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:bg-white/8 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Every Team Type
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Supports enterprise, startup, non profit and distributed work.
              </p>
            </motion.div>

            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:bg-white/8 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center mb-4">
                <Layers className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Every Workplace
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Perfect for office, hybrid and remote environments.
              </p>
            </motion.div>

            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:bg-white/8 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-lg bg-orange-500/20 flex items-center justify-center mb-4">
                <LineChart className="w-6 h-6 text-orange-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Every Sectors
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Trusted by IT, BPO, marketing, EdTech and staffing teams.
              </p>
            </motion.div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      {/* <section className="relative py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8 lg:px-8 bg-black">
        <div className="max-w-4xl mx-auto text-center">

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-[32px] sm:text-[36px] md:text-[42px] lg:text-[48px] font-bold text-white mb-6"
          >
            Ready to See What Your Team Actually Does?
          </motion.h2>

          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl md:text-2xl text-white mb-10"
          >
            Join 2,500+ teams using TeamGrid to boost productivity, eliminate
            guesswork and make data-driven decisions.
          </motion.p>

          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            
            <motion.a
              href="/register"
              className="bg-gradient-to-r from-[#28B4AE] to-[#28B4AE] text-white px-10 py-4 rounded-lg font-semibold text-lg hover:from-[#28B4AE] hover:to-[#00B8A8] transition-all duration-300"
              variants={buttonHoverVariants}
              whileHover="hover"
              whileTap="tap"
            >
              Start Free Trial
            </motion.a>

            
            <motion.a
              href="/book-demo"
              className="bg-white/10 border border-white/20 text-white px-10 py-4 rounded-lg font-semibold text-lg hover:bg-white/15 transition-all duration-300"
              variants={buttonHoverVariants}
              whileHover="hover"
              whileTap="tap"
            >
              Request Demo
            </motion.a>
          </motion.div>
        </div>
      </section> */}

      {/* Trusted By Section */}

      {/* Engagement popup — fires after 30s, once per session (landing only) */}
      <EngagementPopup />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Landing;
